// Import any needed config
import { GEMINI_MODELS } from '../config/constants';

const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";

// REVERT to stable names for the v1beta endpoint
const GEMINI_2_5_PRO_MODEL = "gemini-3.5-flash";
const GEMINI_FLASH_MODEL = "gemini-2.5-flash";

// --- CONCURRENCY QUEUE ---
// This prevents multiple heavy calls from hitting the API exactly at the same time,
// smoothing out your token usage to avoid spikes.
class RequestQueue {
    constructor(concurrency = 1) {
        this.concurrency = concurrency;
        this.running = 0;
        this.queue = [];
    }

    async add(task) {
        if (this.running >= this.concurrency) {
            await new Promise(resolve => this.queue.push(resolve));
        }
        this.running++;
        try {
            return await task();
        } finally {
            this.running--;
            if (this.queue.length > 0) {
                const next = this.queue.shift();
                next();
            }
        }
    }
}

// Global queue instance - limits to 1 active request at a time
const apiQueue = new RequestQueue(1);


// --- CORE API HELPERS ---

export const callGeminiApi = async (
    prompt, 
    generationConfig = {}, 
    images = [], 
    model = GEMINI_FLASH_MODEL,
    maxRetries = 3, // We will dynamically increase this if needed inside
    baseDelay = 2000
) => {
    // Wrap the entire execution in the queue
    return apiQueue.add(async () => {
        let lastError = null;
        
        // Dynamic retry cap: If the API asks us to wait 20s, we might need more attempts or longer timeouts
        let effectiveMaxRetries = maxRetries; 

        for (let attempt = 1; attempt <= effectiveMaxRetries; attempt++) {
            try {
                const parts = [{ text: prompt }];
                images.forEach(imgData => {
                    if (imgData && typeof imgData === 'string') {
                        const [meta, base64] = imgData.split(',');
                        if (meta && base64) {
                            const mimeTypeFromData = meta.match(/:(.*?);/)[1];
                            parts.push({ inlineData: { mimeType: mimeTypeFromData || 'image/jpeg', data: base64 } });
                        }
                    }
                });

                const chatHistory = [{ role: "user", parts }];
                const payload = { 
                    contents: chatHistory,
                    generationConfig
                };
                
               const response = await fetch('https://geminigateway-z6tvc67fca-uc.a.run.app', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        modelName: model,
        contents: chatHistory,
        generationConfig
    })
});

                if (!response.ok) {
                    const errorBody = await response.json();
                    console.error(`Gemini API Error (Attempt ${attempt}/${effectiveMaxRetries}):`, errorBody);
                    
                    // --- SMART RATE LIMIT HANDLING ---
                    if (response.status === 429 || errorBody.error?.message?.includes('quota') || errorBody.error?.message?.includes('rate limit')) {
                        lastError = new Error('RATE_LIMIT');
                        
                        if (attempt < effectiveMaxRetries) {
                            // 1. Check if Google explicitly told us how long to wait
                            const retryMatch = errorBody.error?.message?.match(/retry in ([\d\.]+)s/);
                            let waitTime = 0;

                            if (retryMatch && retryMatch[1]) {
                                // Google said "wait 23.05s". We wait 23.05s + 1s buffer.
                                waitTime = Math.ceil(parseFloat(retryMatch[1]) * 1000) + 1000;
                                console.warn(`Google requested wait time. Pausing for ${waitTime}ms...`);
                                
                                // If the wait time is long, we boost our retry limit to ensure we don't give up too soon
                                effectiveMaxRetries = Math.max(effectiveMaxRetries, attempt + 2);
                            } else {
                                // Default exponential backoff: 2s, 4s, 8s, 16s...
                                waitTime = baseDelay * Math.pow(2, attempt - 1);
                                console.warn(`Standard backoff. Pausing for ${waitTime}ms...`);
                            }

                            // Wait before retrying
                            await new Promise(resolve => setTimeout(resolve, waitTime));
                            continue;
                        }
                    }
                    
                    // Check if it's a timeout or server error (500, 503)
                    if (response.status >= 500 && attempt < effectiveMaxRetries) {
                        lastError = new Error('SERVER_ERROR');
                        console.log(`Server error. Retrying in ${baseDelay}ms...`);
                        await new Promise(resolve => setTimeout(resolve, baseDelay));
                        continue;
                    }
                    
                    throw new Error(errorBody.error?.message || `API request failed with status: ${response.status}`);
                }
                
                const result = await response.json();

                if (result.candidates && result.candidates[0]?.content?.parts?.length > 0) {
                    const text = result.candidates[0].content.parts[0].text;
                    const finishReason = result.candidates[0]?.finishReason;
                    if (generationConfig.responseMimeType === "application/json") {
                        try {
                            let cleanText = text;
                            // Clean markdown code blocks if present
                            if (cleanText.includes('```json')) {
                                cleanText = cleanText.substring(cleanText.indexOf('```json') + 7, cleanText.lastIndexOf('```'));
                            } else if (cleanText.includes('```')) {
                                cleanText = cleanText.substring(cleanText.indexOf('```') + 3, cleanText.lastIndexOf('```'));
                            }
                            
                            cleanText = cleanText.trim();
                            const firstBracket = cleanText.indexOf('[');
                            const firstBrace = cleanText.indexOf('{');
                            
                            let jsonString;
                            if (firstBracket !== -1 && (firstBracket < firstBrace || firstBrace === -1)) {
                                const lastBracket = cleanText.lastIndexOf(']');
                                if (lastBracket === -1) throw new Error("Malformed JSON array in response.");
                                jsonString = cleanText.substring(firstBracket, lastBracket + 1);
                            } else if (firstBrace !== -1) {
                                const lastBrace = cleanText.lastIndexOf('}');
                                if (lastBrace === -1) throw new Error("Malformed JSON object in response.");
                                jsonString = cleanText.substring(firstBrace, lastBrace + 1);
                            } else {
                                throw new Error("Could not find any valid JSON structure in the AI's response.");
                            }
                            
                            return JSON.parse(jsonString);

                        } catch (e) {
                            // A cut-off response (finishReason MAX_TOKENS) always looks like a
                            // JSON parse failure — the text just stops mid-string/mid-object —
                            // but it isn't malformed data, it ran out of output budget. Distinguish
                            // the two so this doesn't get misdiagnosed as a bad-output problem
                            // when it's actually a maxOutputTokens problem.
                            if (finishReason === 'MAX_TOKENS') {
                                console.error(`Response truncated at maxOutputTokens (Attempt ${attempt}/${effectiveMaxRetries}). Raw text:`, text);
                                lastError = new Error('MAX_TOKENS_TRUNCATED');
                                if (attempt < effectiveMaxRetries) {
                                    console.log(`Retrying after truncation...`);
                                    await new Promise(resolve => setTimeout(resolve, baseDelay));
                                    continue;
                                }
                                throw new Error("AI response was cut off before completing (hit the output token limit) — the request needs a higher maxOutputTokens, not a retry.");
                            }
                            console.error(`JSON Parse Error (Attempt ${attempt}/${effectiveMaxRetries}). Raw text:`, text, "Error:", e);
                            lastError = new Error('JSON_PARSE_ERROR');
                            
                            if (attempt < effectiveMaxRetries) {
                                console.log(`Retrying due to parse error...`);
                                await new Promise(resolve => setTimeout(resolve, baseDelay));
                                continue;
                            }
                            throw new Error("AI returned malformed data after multiple attempts.");
                        }
                    }
                    return text;
                } else {
                    console.error("Gemini API returned an unexpected structure:", result);
                    if (result.promptFeedback?.blockReason) {
                        throw new Error(`AI request blocked: ${result.promptFeedback.blockReason}. Adjust your input.`);
                    }
                    
                    lastError = new Error('UNEXPECTED_STRUCTURE');
                    if (attempt < effectiveMaxRetries) {
                        await new Promise(resolve => setTimeout(resolve, baseDelay));
                        continue;
                    }
                    throw new Error("Failed to get content from AI Service after multiple attempts.");
                }
            } catch (error) {
                lastError = error;
                console.error(`Error calling Gemini API (Attempt ${attempt}/${effectiveMaxRetries}):`, error);
                
                if (attempt === effectiveMaxRetries) {
                    throw error;
                }
                
                // Generic wait for non-API errors (network blips)
                await new Promise(resolve => setTimeout(resolve, baseDelay * attempt));
            }
        }
        
        throw lastError || new Error("Failed to get AI response after multiple attempts.");
    });
};

// NOTE (2026-09): Google retired the standalone Imagen `:predict` models on the
// Gemini Developer API for this key/project (confirmed via ListModels — no
// `imagen-*` entries with a `predict` method exist anymore). Image generation
// now lives inside `generateContent` under the "Nano Banana" model family.
// This list — and the request/response handling below — must match whatever
// `imagenGateway` (the cloud function) is actually sending upstream. If the
// gateway still builds an `instances`/`predict` request internally, updating
// this list alone will NOT fix generation; the gateway needs the matching
// `generateContent` rewrite too (see chat notes).
const IMAGEN_MODELS = [
    "gemini-3.1-flash-image",      // Nano Banana 2 (try first)
    "gemini-3-pro-image",          // Nano Banana Pro (higher quality fallback)
    "gemini-2.5-flash-image",      // Nano Banana (base, most widely available)
];

export const callImagenApi = async (prompt) => {
    let lastError = null;

    // Loop through available models as fallbacks
    for (const model of IMAGEN_MODELS) {
        try {
            console.log(`Attempting image gen with model: ${model}...`);

            const response = await fetch('https://us-central1-blacklight-2861c.cloudfunctions.net/imagenGateway', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ model, prompt })
            });

            if (!response.ok) {
                const errorBody = await response.json();
                const errorMessage = errorBody.error?.message || response.statusText;
                
                // If "Not Found" (404) or "Method Not Allowed" (405), try the next model.
                if (response.status === 404 || response.status === 405 || errorMessage.includes('not found') || errorMessage.includes('not supported')) {
                    console.warn(`Model ${model} not available for this key. Switching to fallback...`);
                    lastError = new Error(`Model ${model} unavailable.`);
                    continue; 
                }
                
                // For other errors (like content policy violations), we should stop and report it.
                throw new Error(errorMessage);
            }

            const result = await response.json();

            // generateContent response shape: candidates[0].content.parts[] contains
            // a mix of text and inlineData parts. Find the image part.
            const parts = result?.candidates?.[0]?.content?.parts || [];
            const imagePart = parts.find(p => p.inlineData?.data);

            if (imagePart) {
                console.log(`Success! Generated image with ${model}`);
                const mimeType = imagePart.inlineData.mimeType || 'image/png';
                return `data:${mimeType};base64,${imagePart.inlineData.data}`;
            } else {
                throw new Error("API returned no image data.");
            }

        } catch (error) {
            console.error(`Error with ${model}:`, error);
            lastError = error;
            // Only continue loop if it was a "not found" style error, otherwise we might be hitting a rate limit
            // But for safety, we allow the loop to continue unless it's the last model
        }
    }

    console.error("All image generation models failed.");
    throw lastError || new Error("Image generation failed. Your API key may not have access to Nano Banana image models yet.");
};

// --- CHARACTER NAME EXTRACTION HELPER ---
// Extracts character names from standard screenplay format (ALL-CAPS speaker labels).
// Returns a whitelist string injected into every prompt that references characters,
// eliminating hallucinated names by giving the model an explicit allowlist.
const extractCharacterNames = (scriptContent) => {
    if (!scriptContent) return '';
    
    // Screenplay character cues: line of ALL-CAPS words (2-30 chars), 
    // optionally followed by (V.O.) / (O.S.) / (CONT'D) etc.
    // We look for lines that are predominantly uppercase letters, possibly with spaces/apostrophes.
    const lines = scriptContent.split('\n');
    const nameSet = new Set();
    
    const charCuePattern = /^\s{10,}([A-Z][A-Z\s\'\-\.]{1,28}[A-Z\.])(?:\s*\(.*\))?\s*$/;
    
    lines.forEach(line => {
        const match = line.match(charCuePattern);
        if (match) {
            // Strip trailing parentheticals and trim
            const raw = match[1].trim();
            // Exclude common non-name all-caps strings
            const excluded = new Set([
                'INT', 'EXT', 'CUT TO', 'FADE IN', 'FADE OUT', 'SMASH CUT',
                'DISSOLVE TO', 'TITLE CARD', 'SUPER', 'END', 'THE END',
                'CONTINUED', 'FLASHBACK', 'INTERCUT', 'MONTAGE', 'SERIES OF SHOTS',
                'BACK TO SCENE', 'LATER', 'CONTINUOUS', 'MOMENTS LATER'
            ]);
            if (!excluded.has(raw) && raw.length >= 2 && raw.length <= 30) {
                nameSet.add(raw);
            }
        }
    });

    // Sort by frequency: count how many times each name appears as a cue
    const counts = {};
    nameSet.forEach(name => {
        counts[name] = (scriptContent.match(new RegExp('\\b' + name + '\\b', 'g')) || []).length;
    });

    // Return top 20 by frequency, sorted descending — cuts noise from one-off extras
    const sorted = [...nameSet].sort((a, b) => (counts[b] || 0) - (counts[a] || 0)).slice(0, 20);
    return sorted.join(', ') || 'Not extractable — treat all character references with caution';
};

// Enhanced Script Analysis Function with Credit Integration

export const generateEnhancedScriptAnalysisLLM = async (scriptContent, filmData, audienceData, credits, deductCredit, setNotification) => {
    // Credit check first
    const creditCost = 5; // High-quality script analysis should cost more
    if (credits < creditCost) {
        setNotification({ 
            type: 'error', 
            message: `Insufficient credits. Script analysis requires ${creditCost} credits. You have ${credits}.` 
        });
        throw new Error('Insufficient credits');
    }

    // Andromeda-era story intelligence — every scene is evaluated for its signal value
    // to the algorithm, not just its marketing potential in the traditional sense.
    
    // Pre-extract character names to prevent hallucination
    const scriptCharNames = extractCharacterNames(scriptContent);
    
    const matrixContext = audienceData?.creative_matrix?.length > 0
        ? audienceData.creative_matrix.map((m, i) => `Hook ${i+1} [${m.hook_name}]: Trigger=${m.psychological_trigger}, Visuals=${m.visual_direction}, Copy=${m.copy_angle}`).join('\n')
        : 'No Creative Matrix yet — analyze for broad hook diversity.';

    const prompt = `
    You are an elite film marketing strategist operating in the Andromeda algorithmic era.
    "Creative as Targeting" is the governing principle — the algorithm self-selects the right audience based entirely on what the creative signals. Your job is to forensically analyze this script and identify which scenes, moments, and dialogue lines carry the strongest algorithmic signal for each audience segment.

    FILM INFO:
    - Title: "${filmData.title}"
    - Genre: "${filmData.genre}"
    - Logline: "${filmData.logline}"
    - Primary Target: "${audienceData?.target_audience_breakdown?.primary_audience_profile || 'General audience'}"
    - Psychographics: "${(audienceData?.target_audience_breakdown?.psychographics || []).join(', ') || 'Not specified'}"

    ANDROMEDA CREATIVE MATRIX (map every scene to one of these hooks):
    ${matrixContext}

    ACTUAL SCRIPT CONTENT:
    ${scriptContent.substring(0, 12000)}

    ╔══════════════════════════════════════════════════════════════╗
    ║  CHARACTER NAME WHITELIST — HARD CONSTRAINT                  ║
    ║  Only these names exist in this script. Do not use any name  ║
    ║  not on this list. If a name you want to use is absent,      ║
    ║  describe the character by role/archetype instead.           ║
    ╚══════════════════════════════════════════════════════════════╝
    VALID CHARACTER NAMES: ${scriptCharNames}

    INSTRUCTIONS:
    1. Read the ACTUAL script — only use real scenes, dialogue, and the character names from the whitelist above.
    2. For every marketable moment, identify which Andromeda hook it executes and what format it lends itself to.
    3. Campaign hooks must be written as social copy hooks that signal to the algorithm, not generic taglines.
    4. Identify which scenes have creative variety (different emotional registers) — Andromeda needs diversity across tension, warmth, humor, conflict, triumph to find multiple audience segments.
    5. CRITICAL: Never invent character names, dialogue, or scene descriptions not found in the script text.

    Return valid JSON only.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            marketable_moments: {
                type: "ARRAY",
                description: "Specific scenes from the actual script with marketing potential",
                items: {
                    type: "OBJECT",
                    properties: {
                        scene_description: { type: "STRING", description: "Actual scene from the script" },
                        page_reference: { type: "STRING", description: "Approximate location in script" },
                        marketing_potential: { type: "STRING", description: "Why this scene is marketable (High/Medium/Low)" },
                        target_demographic: { type: "STRING", description: "Which audience segment this appeals to" },
                        andromeda_hook: { type: "STRING", description: "Which Creative Matrix hook this scene executes." },
                        best_format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Any"], description: "Optimal format for this moment's algorithmic signal." },
                        emotional_register: { type: "STRING", description: "Tension | Warmth | Humor | Conflict | Awe | Triumph | Intimacy — for creative diversity scoring." },
                        suggested_tactics: { 
                            type: "ARRAY", 
                            items: { type: "STRING" },
                            description: "Specific social media tactics for this scene"
                        },
                        trailer_worthy: { type: "BOOLEAN", description: "Should this be in the trailer?" }
                    },
                    required: ["scene_description", "marketing_potential", "target_demographic", "andromeda_hook", "best_format", "emotional_register", "suggested_tactics", "trailer_worthy"]
                }
            },
            quotable_dialogue: {
                type: "ARRAY",
                description: "ACTUAL quotes from the script that are marketable",
                items: {
                    type: "OBJECT", 
                    properties: { 
                        quote: { type: "STRING", description: "Exact quote from the script" },
                        character: { type: "STRING", description: "Character name from script who says this" }, 
                        context: { type: "STRING", description: "When/why this is said in the story" },
                        marketing_appeal: { type: "STRING", description: "Why this quote is marketable" },
                        social_media_potential: { type: "STRING", description: "How to use on social media" }
                    },
                    required: ["quote", "character", "context", "marketing_appeal", "social_media_potential"]
                }
            },
            character_marketing_profiles: {
                type: "ARRAY",
                description: "Marketing angles for ACTUAL characters in the script",
                items: {
                    type: "OBJECT",
                    properties: { 
                        character: { type: "STRING", description: "Actual character name from script" },
                        character_arc: { type: "STRING", description: "Their story arc in the script" },
                        marketing_angle: { type: "STRING", description: "How to market this character" },
                        audience_appeal: { type: "STRING", description: "Which demographics relate to this character" },
                        merchandise_potential: { type: "STRING", description: "Merchandising opportunities" }
                    },
                    required: ["character", "character_arc", "marketing_angle", "audience_appeal"]
                }
            },
            genre_positioning: {
                type: "OBJECT",
                description: "How this script fits in current market",
                properties: { 
                    genre_elements: { type: "ARRAY", items: { type: "STRING" }, description: "Actual genre elements present in script" },
                    comparable_films: { type: "ARRAY", items: { type: "STRING" }, description: "Similar successful films" },
                    unique_selling_points: { type: "ARRAY", items: { type: "STRING" }, description: "What makes this script different" },
                    market_positioning: { type: "STRING", description: "How to position against competitors" }
                },
                required: ["genre_elements", "comparable_films", "unique_selling_points", "market_positioning"]
            },
            optimal_release_strategy: {
                type: "OBJECT", 
                properties: { 
                    recommended_season: { type: "STRING", description: "Best season to release based on script content" },
                    platform_strategy: { type: "STRING", description: "Theatrical vs streaming vs hybrid" },
                    festival_strategy: { type: "STRING", description: "Film festival approach" },
                    avoid_periods: { type: "ARRAY", items: { type: "STRING" }, description: "When not to release" }
                },
                required: ["recommended_season", "platform_strategy", "festival_strategy"]
            },
            campaign_hooks: {
                type: "OBJECT",
                properties: {
                    primary_hook: { type: "STRING", description: "Main marketing hook based on script" },
                    secondary_hooks: { type: "ARRAY", items: { type: "STRING" }, description: "Alternative angles" },
                    tagline_suggestions: { type: "ARRAY", items: { type: "STRING" }, description: "Potential taglines" },
                    poster_concepts: { type: "ARRAY", items: { type: "STRING" }, description: "Poster/artwork ideas" },
                    trailer_structure: { type: "STRING", description: "How to structure the trailer" }
                },
                required: ["primary_hook", "secondary_hooks", "tagline_suggestions"]
            },
            risk_assessment: {
                type: "OBJECT",
                properties: {
                    content_concerns: { type: "ARRAY", items: { type: "STRING" }, description: "Potential content issues" },
                    rating_prediction: { type: "STRING", description: "Likely MPAA rating" },
                    marketing_challenges: { type: "ARRAY", items: { type: "STRING" }, description: "Marketing obstacles" },
                    mitigation_strategies: { type: "ARRAY", items: { type: "STRING" }, description: "How to address challenges" }
                },
                required: ["content_concerns", "rating_prediction", "marketing_challenges"]
            }
        },
        required: ["marketable_moments", "quotable_dialogue", "character_marketing_profiles", "genre_positioning", "optimal_release_strategy", "campaign_hooks", "risk_assessment"]
    };

    try {
        // Call the AI with enhanced prompt
        const result = await callGeminiApi(prompt, { 
            responseMimeType: "application/json", 
            responseSchema: schema 
        }, [], GEMINI_2_5_PRO_MODEL);

        // Deduct credits on successful analysis
        const success = await deductCredit(creditCost);
        if (!success) {
            throw new Error("Credit deduction failed");
        }

        setNotification({ 
            type: 'success', 
            message: `Script analysis complete! ${creditCost} credits used.` 
        });

        return result;

    } catch (error) {
        console.error('Script analysis failed:', error);
        throw error;
    }
};

// Enhanced Character Analysis Function
export const generateCharacterAnalysisLLM = async (scriptContent, selectedCharacters, filmData, credits, deductCredit, setNotification, audienceData = null) => {
    // Credit check
    const creditCost = 3;
    if (credits < creditCost) {
        setNotification({ 
            type: 'error', 
            message: `Insufficient credits. Character analysis requires ${creditCost} credits. You have ${credits}.` 
        });
        throw new Error('Insufficient credits');
    }

    // LOGIC FIX: If frontend extraction failed (selectedCharacters is empty), we ask AI to find them.
    const hasPreSelectedChars = selectedCharacters && selectedCharacters.length > 0;
    
    const characterInstruction = hasPreSelectedChars 
        ? `FOCUS ONLY ON THESE CHARACTERS: ${selectedCharacters.join(', ')}`
        : `IDENTIFY THE TOP 4-6 MOST PROMINENT CHARACTERS from the script automatically.`;

    // Pre-extract character names from the script to prevent hallucination.
    // This whitelist is injected as a hard constraint in the prompt.
    const extractedNames = extractCharacterNames(scriptContent);
    
    // Pull Andromeda context if available
    const charMatrix = audienceData?.creative_matrix || [];
    const charMatrixContext = charMatrix.length > 0
        ? charMatrix.map((m, i) => `Hook ${i+1} [${m.hook_name}]: ${m.psychological_trigger}`).join('\n')
        : 'No Creative Matrix — identify hook potential per character independently.';
    const charPrimaryAud = audienceData?.target_audience_breakdown?.primary_audience_profile || 'General audience';

    const prompt = `
    You are an elite film marketing strategist in the Andromeda algorithmic era.
    Analyze the characters in this script not just for general marketability, but for their specific ALGORITHMIC VALUE — which audience segment does each character's arc signal to the algorithm? Which Creative Matrix hook do they embody? What format amplifies them best?

    FILM: "${filmData.title}" (${filmData.genre})
    PRIMARY AUDIENCE: "${charPrimaryAud}"
    ${characterInstruction}

    ANDROMEDA CREATIVE MATRIX (map each character to a hook):
    ${charMatrixContext}

    SCRIPT CONTENT (Excerpt):
    ${scriptContent.substring(0, 15000)}

    ╔══════════════════════════════════════════════════════════════╗
    ║  CHARACTER NAME WHITELIST — HARD CONSTRAINT                  ║
    ║  The following names were extracted directly from the script. ║
    ║  YOU MAY ONLY USE NAMES FROM THIS LIST. Any character name    ║
    ║  not on this list DOES NOT EXIST in this script.             ║
    ║  DO NOT invent, infer, or generalize character names.        ║
    ╚══════════════════════════════════════════════════════════════╝
    VALID CHARACTER NAMES: ${extractedNames}

    INSTRUCTIONS:
    1. Extract actual dialogue, personality traits, and story arcs from the script.
    2. Determine "Marketability Score" (1-10) based on audience appeal AND algorithmic signal strength.
    3. Suggest "Casting Comparisons" as shorthand for the talent type needed.
    4. For each character, identify: which Andromeda hook they execute, which audience segment they will find, and which social media format amplifies them best.
    5. Identify the single most algorithm-ready moment for each character — use only scenes and dialogue actually in the provided script.
    6. If you cannot find enough script content to confidently name a character, return only the characters you CAN verify. Do not pad the list.

    Provide detailed character profiles in JSON format:
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            character_profiles: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        name: { type: "STRING", description: "Character name" },
                        archetype: { type: "STRING" },
                        marketability_score: { type: "NUMBER" },
                        casting_comparisons: { type: "ARRAY", items: { type: "STRING" } },
                        narrative_roi: { type: "STRING", description: "Why this character matters to the campaign." },
                        personality_traits: { type: "ARRAY", items: { type: "STRING" } },
                        best_quotes: { type: "ARRAY", items: { type: "STRING" } },
                        character_arc: { type: "STRING" },
                        target_demographic: { type: "STRING" },
                        marketing_angles: { type: "ARRAY", items: { type: "STRING" } },
                        andromeda_hook: { type: "STRING", description: "Which Creative Matrix hook this character embodies." },
                        algorithm_signal_strength: { type: "STRING", enum: ["High", "Medium", "Low"], description: "How strongly this character signals to the Andromeda algorithm." },
                        best_format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image"], description: "The format that amplifies this character's hook most effectively." },
                        signature_algorithm_moment: { type: "STRING", description: "The single scene from the script most likely to make Andromeda self-select the right audience if captured as a BTS drop." }
                    },
                    required: ["name", "personality_traits", "best_quotes", "character_arc", "target_demographic", "marketing_angles", "archetype", "marketability_score", "andromeda_hook", "algorithm_signal_strength", "best_format", "signature_algorithm_moment"]
                }
            }
        },
        required: ["character_profiles"]
    };

    try {
        const result = await callGeminiApi(prompt, { 
            responseMimeType: "application/json", 
            responseSchema: schema 
        }, [], GEMINI_2_5_PRO_MODEL);

        await deductCredit(creditCost);
        
        setNotification({ 
            type: 'success', 
            message: `Character analysis complete! ${creditCost} credits used.` 
        });

        return result;

    } catch (error) {
        console.error('Character analysis failed:', error);
        throw error;
    }
};

export const generateMarketIntelligenceLLM = async (filmData, audienceData) => {
    const prompt = `
    As a film industry analyst, provide comprehensive market intelligence.
    FILM: "${filmData.title}" (${filmData.genre})
    TARGET AUDIENCE: ${audienceData?.target_audience_breakdown?.primary_audience_profile || 'General'}
    Generate detailed market analysis in JSON format.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            competitive_landscape: {
                type: "OBJECT",
                properties: {
                    direct_competitors: { type: "ARRAY", items: { type: "STRING" } },
                    competitive_advantages: { type: "ARRAY", items: { type: "STRING" } }
                }
            },
            industry_trends: {
                type: "OBJECT",
                properties: {
                    hot_topics: { type: "ARRAY", items: { type: "STRING" } },
                    emerging_opportunities: { type: "ARRAY", items: { type: "STRING" } }
                }
            },
            distribution_insights: {
                type: "OBJECT",
                properties: {
                    platform_recommendations: { type: "ARRAY", items: { type: "STRING" } },
                    theatrical_strategy: { type: "STRING" },
                    streaming_positioning: { type: "STRING" }
                }
            }
        },
        required: ["competitive_landscape", "industry_trends", "distribution_insights"]
    };

    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [], GEMINI_2_5_PRO_MODEL);
};


export const generateProductionMarketingLLM = async (filmData, audienceData, scriptContent) => {
    
    // Safety Check: Prevent hallucinations if script is missing
    if (!scriptContent || scriptContent.length < 50) {
        throw new Error("Cannot generate Production Playbook: No script content detected.");
    }

    // Capture a significant chunk of the script to ensure character and scene depth
    const scriptSnippet = scriptContent.substring(0, 20000);

    const prompt = `
    You are a Unit Publicist and Creative Director. Create a high-end, tactical "Production Marketing Playbook" for the film: "${filmData.title}".
    
    SOURCE MATERIAL (ACTUAL SCRIPT EXCERPT):
    """
    ${scriptSnippet}
    """

    TASK: Analyze the script excerpt above to identify the specific assets required for a multi-channel marketing campaign.

    CRITICAL INSTRUCTIONS:
    1. READ the script excerpt to identify top 3-4 ACTUAL characters and SPECIFIC high-impact scenes.
    2. UNIT PUBLICIST MANIFEST: Identify 6 specific BTS moments from this script that are "viral-ready" (e.g., specific SFX setups, tense character standoffs).
    3. TALENT EPK GUIDE: Provide 3 non-generic, psychologically-focused interview questions for the VERIFIED characters found in the text.
    4. KEY ART STILLS: List 3 specific moments/frames from the script that must be captured by the Unit Stills Photographer for the Teaser Poster.
    5. STRICTLY FORBIDDEN: Do not invent character names. Only use what is in the provided text.

    TARGET AUDIENCE: ${audienceData?.target_audience_breakdown?.primary_audience_profile || 'General audience'}
    
    Your entire response must be a single, valid JSON object matching the schema below.
    `;

    const schema = {
        type: "OBJECT", 
        properties: {
            behind_scenes_strategy: {
                type: "OBJECT",
                properties: {
                    capture_manifest: { 
                        type: "ARRAY", 
                        items: { 
                            type: "OBJECT", 
                            properties: { 
                                moment: { type: "STRING", description: "Specific scene or setup to capture (e.g., 'The hallway strobe light sequence')" },
                                purpose: { type: "STRING", description: "Marketing goal (e.g., 'Viral atmosphere hook')" }
                            },
                            required: ["moment", "purpose"]
                        }
                    },
                    social_hooks: { type: "ARRAY", items: { type: "STRING" } }
                },
                required: ["capture_manifest", "social_hooks"]
            },
            interview_talking_points: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        person: { type: "STRING", description: "Verified character name." },
                        psychology_questions: { 
                            type: "ARRAY", 
                            items: { type: "STRING" },
                            description: "Questions about the character's internal struggle in the script."
                        },
                        marketing_hook: { type: "STRING", description: "The specific 'angle' for this character's PR." }
                    },
                    required: ["person", "psychology_questions", "marketing_hook"]
                }
            },
            key_art_stills_list: {
                type: "ARRAY",
                items: { 
                    type: "OBJECT", 
                    properties: { 
                        scene: { type: "STRING", description: "Detailed description of the still to capture." },
                        visual_goal: { type: "STRING", description: "Art direction goal (e.g., 'Conveying isolation')." }
                    },
                    required: ["scene", "visual_goal"]
                }
            }
        },
        required: ["behind_scenes_strategy", "interview_talking_points", "key_art_stills_list"]
    };

    return await callGeminiApi(prompt, { 
        responseMimeType: "application/json", 
        responseSchema: schema 
    }, [], GEMINI_2_5_PRO_MODEL); // Utilizing the Pro model for deep narrative reasoning
};

export const generateTalentAnalysisLLM = async (filmData) => {
    const castList = filmData.stars || "Cast TBD";
    const genre = filmData.genre || "General";
    
    const prompt = `
    You are a Hollywood Casting Director and Sales Agent.
    
    CAST LIST: ${castList}
    
    TASK: Identify the **Top 3 most marketable stars** from the list above for a "${genre}" film. 
    Ignore minor supporting cast if the list is long. Focus on the names that drive financing and distribution.

    Return a JSON object with this exact structure for those top 3 (or fewer):
    {
        "cast_members": [
            {
                "name": "Actor Name",
                "role": "Lead" or "Supporting",
                "knownFor": ["Title 1", "Title 2", "Title 3"] (3 biggest relevant hits),
                "socialReach": "Estimated follower count string (e.g. '8.2M+') based on public fame",
                "demographic": "Specific audience segment they attract (e.g. 'Gen Z', 'Urban Female 25-45')"
            }
        ],
        "combined_reach": "Total estimated reach string (e.g. '15M+')",
        "casting_synergy": {
            "title": "Short, punchy title for the pairing (e.g. 'Multi-Generational Appeal')",
            "description": "2 sentences explaining why this specific combination of actors maximizes sales value."
        },
        "marketability_score": Number between 1-10 based on genre fit and star power.
    }
    `;

    return await callGeminiApi(prompt, { responseMimeType: "application/json" });
};


export const generateDossierHealthCheckLLM = async (allData) => {
    // 1. SAFEGUARD: Handle missing data gracefully
    if (!allData || !allData.filmData) {
        throw new Error("Missing project data for Health Check.");
    }

    const { filmData, budgetPlan, videoAnalysis, revenueForecast, visualAnalysis } = allData;

    // 2. CREATE A "LEAN" FILM OBJECT (Crucial for Token Quota)
    // We explicitly extract ONLY strategic text. 
    // We purposely IGNORE 'script_content', 'ad_concepts' (images), and 'video_keyframes_urls'.
    const leanFilmData = {
        title: filmData.title || "Untitled Project",
        logline: filmData.logline || "",
        genre: filmData.genre || "",
        themes: filmData.themes || "",
        budgetLevel: filmData.budgetLevel || "",
        // Only grab the latest audience summary, not the whole history array
        audience_insight: filmData.ai_analyses?.length > 0 
            ? filmData.ai_analyses[filmData.ai_analyses.length - 1].analysis?.target_audience_breakdown?.primary_audience_profile 
            : "Not yet analyzed",
        // Only grab the latest press strategy summary
        press_angle: filmData.press_strategy?.executive_summary || "Not yet analyzed"
    };

    // 3. SUMMARIZE BUDGET (Don't send the whole table)
    const leanBudget = budgetPlan ? {
        total_budget: budgetPlan.budget_allocation_table?.reduce((sum, i) => sum + (i.dollar_amount || 0), 0) || 0,
        top_kpis: budgetPlan.executive_snapshot?.kpis?.slice(0, 3) || [], // Limit to top 3
        strategy_summary: budgetPlan.executive_snapshot?.rationale || ""
    } : "Budget not created";

    // 4. SUMMARIZE REVENUE (Just the top-line numbers)
    const leanRevenue = revenueForecast?.data?.theatrical_release_projections ? {
        domestic_forecast: revenueForecast.data.theatrical_release_projections.domestic_box_office_forecast,
        global_forecast: revenueForecast.data.theatrical_release_projections.international_box_office_forecast,
        roi_insight: revenueForecast.data.underserved_opportunities || ""
    } : "Revenue not forecast";

    // 5. SUMMARIZE CREATIVE ASSETS (Just the scores/text, no images)
    const leanCreative = {
        video_buzz: videoAnalysis?.PredictedBuzzFactor || "N/A",
        video_impression: videoAnalysis?.OverallImpression || "N/A",
        poster_verdict: visualAnalysis?.recommendation || "N/A",
        poster_insight: visualAnalysis?.insight || "N/A"
    };

    // 6. CONSTRUCT OPTIMIZED PROMPT
    const prompt = `
    You are BLACKBOX, a senior film marketing strategist. Perform a "Health Check" on this film project.
    
    PROJECT SNAPSHOT:
    ${JSON.stringify(leanFilmData)}

    FINANCIAL STATUS:
    ${JSON.stringify(leanBudget)}
    ${JSON.stringify(leanRevenue)}

    CREATIVE ASSET STATUS:
    ${JSON.stringify(leanCreative)}

    TASK:
    Analyze the alignment between the creative assets, budget, and audience targets.
    
    OUTPUT:
    Return a valid JSON object with exactly these keys:
    - "overallHealth": One concise sentence summarizing the project's viability.
    - "biggestStrength": The single strongest asset or strategy found in the data.
    - "greatestRisk": The most critical gap or risk (e.g., "High budget but low buzz", "No audience defined").
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            overallHealth: { type: "STRING" },
            biggestStrength: { type: "STRING" },
            greatestRisk: { type: "STRING" }
        },
        required: ["overallHealth", "biggestStrength", "greatestRisk"]
    };

    // 7. EXECUTE WITH BACKOFF
    // Use the queue and retry logic you already have in callGeminiApi
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};


// --- NEW AI-POWERED PLACEHOLDER GENERATORS ---

export const generateVisualPlaceholderLLM = async (filmData) => {
    const prompt = `
    You are a top-tier film marketing executive and creative director.
    A dossier is being prepared for the film "${filmData.title}", but the final poster is not ready.
    Based on the film's data, write a compelling, confident, and concise paragraph (40-60 words) describing the intended VISUAL IDENTITY and art direction for the poster and key art.
    The tone must be strategic and evocative. Start your response by referencing the film's title directly (e.g., "For '${filmData.title}', the key art will..."). State the vision as a fact, not a plan.

    FILM DATA:
    - Title: "${filmData.title}"
    - Logline: "${filmData.logline}"
    - Genre: "${filmData.genre}"
    - Key Themes: "${filmData.themes}"

    Your output must be a single paragraph of text. Do not use markdown.
    `;
    return await callGeminiApi(prompt, {}, [], GEMINI_FLASH_MODEL);

};

export const generateTrailerPlaceholderLLM = async (filmData) => {
    const prompt = `
    You are an expert trailer editor and marketing strategist.
    A dossier is being prepared for the film "${filmData.title}", but the trailer has not been analyzed yet.
    Based on the film's data, write a compelling, confident, and concise paragraph (50-70 words) describing the creative and editorial direction for the main trailer.
    Focus only on the creative vision (tone, pacing, sound design, emotional journey). Do NOT mention the trailer's production status (e.g., "in production," "is being developed").
    Begin your response directly with a phrase like "The official trailer for '${filmData.title}' will..."

    FILM DATA:
    - Title: "${filmData.title}"
    - Logline: "${filmData.logline}"
    - Genre: "${filmData.genre}"
    - Key Themes: "${filmData.themes}"

    Your output must be a single paragraph of text. Do not use markdown.
    `;
    return await callGeminiApi(prompt, {}, [], GEMINI_FLASH_MODEL);

};


// --- SPECIFIC GENERATORS ---

export const generateCarouselCaptionLLM = async (commentTexts, filmData) => {
    const prompt = `
    You are a social media manager for the film "${filmData.title}". You have selected ${commentTexts.length} highly positive audience comments to feature in an Instagram/Facebook carousel. The comments are:
    ${commentTexts.map(text => `- "${text}"`).join('\n')}

    Your task is to write one single, compelling caption for the *first slide* of the carousel. The caption should:
    1.  Be enthusiastic and engaging.
    2.  Summarize the overwhelmingly positive sentiment.
    3.  Encourage users to "swipe through" to read the actual comments.
    4.  Include relevant hashtags for the film.

    Return a single, valid JSON object with the generated caption.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            caption: { type: "STRING" }
        },
        required: ["caption"]
    };
    
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};



// NEW: LLM for the initial curation step
export const generateCommentAnalysisLLM = async (screenshotBase64) => {
    const prompt = `
    Analyze the provided screenshot of an audience comment. Your tasks are:
    1.  Extract the exact text of the comment.
    2.  Determine the sentiment. Choose one: "Positive", "Very Positive", "Rave".
    3.  Suggest 2-3 relevant tags (e.g., "acting", "cinematography", "emotional", "funny").
    Return a single, valid JSON object.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            extracted_text: { type: "STRING" },
            sentiment: { type: "STRING", enum: ["Positive", "Very Positive", "Rave"] },
            suggested_tags: { type: "ARRAY", items: { type: "STRING" } }
        },
        required: ["extracted_text", "sentiment", "suggested_tags"]
    };
    
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [screenshotBase64]);
};

// REVISED: LLM for the remixing step
export const generateRemixVariationsLLM = async (curatedText, filmData) => {
    const prompt = `
    You are an expert social media manager for the film "${filmData.title}". A fan left the following positive comment: "${curatedText}".

    Your task is to generate three distinct social media post variations (Instagram, X/Twitter, Facebook). For each post, provide:
    1.  A short, catchy headline.
    2.  Engaging body copy that incorporates or references the fan's quote.
    3.  A detailed prompt for an AI image generator to create a visually compelling background graphic that reflects the film's genre (${filmData.genre}) and the tone of the comment.
    
    Return a valid JSON object containing an array of posts.
    `;
    
    const schema = {
        type: "OBJECT",
        properties: {
            posts: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", enum: ["Instagram", "X", "Facebook"] },
                        headline: { type: "STRING" },
                        body: { type: "STRING" },
                        image_prompt: { type: "STRING" }
                    },
                    required: ["platform", "headline", "body", "image_prompt"]
                }
            }
        },
        required: ["posts"]
    };
    
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};


export const generateAudienceAnalysisLLM = async (filmData) => {
    const prompt = `
    You are an elite film marketing strategist AI operating in the modern algorithmic era (Meta's Andromeda, TikTok's For You engine). 
    Detailed interest targeting is dead. The algorithm relies entirely on "Creative as Targeting."
    
    Based on the input below, generate a comprehensive strategic report focusing on broad demographic appeal, creative hook diversity, and asset allocation to feed machine-learning ad platforms.

    INPUT FILM DETAILS:
    • Title: "${filmData.title}"
    • Logline: "${filmData.logline}"
    • Genre/Subgenre: "${filmData.genre}"
    • Budget Level: "${filmData.budgetLevel}"
    • Cast & Fanbases: "${filmData.stars || 'Not specified'}"
    • Key Themes / Special Elements: "${filmData.themes || 'Not specified'}"

    CRITICAL INSTRUCTIONS:
    1. RETURN VALID JSON ONLY.
    2. Focus the 'creative_matrix' on 3 distinct narrative hooks that appeal to different psychological drivers (e.g., Thrill-seeking vs. Emotional Resonance).
    3. The 'asset_allocation' must recommend a percentage split across formats (Vertical Video, Carousel, Static) totaling 100%.
    `;

    const festivalItemSchema = {
        type: "OBJECT",
        properties: {
            name: { type: "STRING" },
            dates: { type: "STRING" },
            deadline: { type: "STRING" }
        },
        required: ["name", "dates", "deadline"]
    };

    const schema = {
      type: "OBJECT",
      properties: {
        executive_summary: { type: "ARRAY", items: { type: "STRING" } },
        target_audience_breakdown: {
          type: "OBJECT",
          properties: {
            primary_audience_profile: { type: "STRING" },
            secondary_audience_profile: { type: "STRING" },
            psychographics: { type: "ARRAY", items: { type: "STRING" } },
            comparable_films: { type: "ARRAY", items: { type: "STRING" } }
          },
           required: ["primary_audience_profile", "secondary_audience_profile", "psychographics", "comparable_films"]
        },
        top_us_theatrical_markets: { type: "ARRAY", items: { type: "STRING" } },
        top_film_festivals: {
          type: "OBJECT",
          properties: {
            tier_1: { type: "ARRAY", items: festivalItemSchema },
            tier_2: { type: "ARRAY", items: festivalItemSchema },
            tier_3: { type: "ARRAY", items: festivalItemSchema }
          },
          required: ["tier_1", "tier_2", "tier_3"]
        },
        // NEW: The Creative Matrix replaces old targeting blueprints
        creative_matrix: {
            type: "ARRAY",
            items: {
                type: "OBJECT",
                properties: {
                    hook_name: { type: "STRING", description: "e.g., 'The Emotional Core'" },
                    psychological_trigger: { type: "STRING", description: "Why people click this." },
                    visual_direction: { type: "STRING", description: "What the asset should look like." },
                    copy_angle: { type: "STRING", description: "Tone of the caption." }
                },
                required: ["hook_name", "psychological_trigger", "visual_direction", "copy_angle"]
            }
        },
        // NEW: Asset formatting strategy
        asset_allocation: {
            type: "OBJECT",
            properties: {
                vertical_video_percent: { type: "NUMBER" },
                carousel_percent: { type: "NUMBER" },
                static_image_percent: { type: "NUMBER" },
                rationale: { type: "STRING" }
            },
            required: ["vertical_video_percent", "carousel_percent", "static_image_percent", "rationale"]
        }
      },
      required: ["executive_summary", "target_audience_breakdown", "top_us_theatrical_markets", "top_film_festivals", "creative_matrix", "asset_allocation"]
    };

    const model = GEMINI_2_5_PRO_MODEL; // Keep using the Pro model for deep strategy
    const config = { responseMimeType: "application/json", responseSchema: schema };
    
    return await callGeminiApi(prompt, config, [], model);
};

export const generateRevenuePotentialReportLLM = async (filmData, marketingSpend, releaseDate, theaterCount, targetMarkets, productionBudget, distributionMode) => {
    
    // Mode-Specific Instructions
    let modeInstructions = "";
    if (distributionMode === 'hybrid') {
        modeInstructions = `
        FOCUS: ALTERNATIVE / HYBRID DISTRIBUTION.
        - Model a "Roadshow" or "Event Cinema" strategy (touring with the film, Q&As, four-walling).
        - Revenue comes from ticket splits (often higher percentage) + merchandise sales at events.
        - "Theaters" in this context refers to stops on the tour or one-off event screenings.
        - Analyze the viability of a "Direct-to-Fan" model.
        `;
    } else {
        modeInstructions = `
        FOCUS: TRADITIONAL THEATRICAL RELEASE.
        - Uses standard exhibition splits (approx 50/50).
        - Theater count (${theaterCount}) is the primary driver.
        `;
    }

    const prompt = `
    As a veteran film finance strategist, model the revenue potential for:
    FILM: "${filmData.title}" (${filmData.genre})
    CURRENT BUDGET ASK: $${Number(productionBudget).toLocaleString()}
    MARKETING (P&A): $${Number(marketingSpend).toLocaleString()}
    STRATEGY: ${distributionMode.toUpperCase()}
    THEATERS/STOPS: ${theaterCount}
    
    ${modeInstructions}
    
    TASK: Provide a 3-tier forecast (Conservative, Realistic, Breakout) and a "Greenlight Analysis".

    CRITICAL: You MUST output a valid JSON object matching this exact schema:
    {
        "scenarios": {
            "conservative": {
                "domestic_box_office": number,
                "international_box_office": number,
                "opening_weekend": number,
                "rationale": "Why this happens"
            },
            "realistic": {
                "domestic_box_office": number,
                "international_box_office": number,
                "opening_weekend": number,
                "rationale": "Why this happens"
            },
            "breakout": {
                "domestic_box_office": number,
                "international_box_office": number,
                "opening_weekend": number,
                "rationale": "Why this happens"
            }
        },
        "greenlight_analysis": {
            "recommended_budget_cap": number, // The maximum production budget to stay profitable in the 'Conservative' scenario
            "profitability_verdict": "Likely, Risky, or Unlikely at current budget",
            "break_even_point": number // Total gross needed to cover P&A + Budget
        },
        "recoupment_structure": {
            "exhibitor_share_percent": 0.50, // Adjust based on mode (Hybrid might be lower if four-walling)
            "distribution_fee_percent": 0.30,
            "note": "Split structure note"
        },
        "ancillary_revenue_multipliers": {
            "conservative": 0.30,
            "realistic": 0.60,
            "breakout": 1.20,
            "explanation": "Home video/streaming revenue impact"
        },
        "comparable_films": [
            { "title": "Film A", "box_office": "$10M", "scenario_match": "Conservative" },
            { "title": "Film B", "box_office": "$25M", "scenario_match": "Realistic" },
            { "title": "Film C", "box_office": "$80M", "scenario_match": "Breakout" }
        ]
    }
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            scenarios: {
                type: "OBJECT",
                properties: {
                    conservative: { type: "OBJECT", properties: { domestic_box_office: { type: "NUMBER" }, international_box_office: { type: "NUMBER" }, opening_weekend: { type: "NUMBER" }, rationale: { type: "STRING" } }, required: ["domestic_box_office", "international_box_office", "opening_weekend", "rationale"] },
                    realistic: { type: "OBJECT", properties: { domestic_box_office: { type: "NUMBER" }, international_box_office: { type: "NUMBER" }, opening_weekend: { type: "NUMBER" }, rationale: { type: "STRING" } }, required: ["domestic_box_office", "international_box_office", "opening_weekend", "rationale"] },
                    breakout: { type: "OBJECT", properties: { domestic_box_office: { type: "NUMBER" }, international_box_office: { type: "NUMBER" }, opening_weekend: { type: "NUMBER" }, rationale: { type: "STRING" } }, required: ["domestic_box_office", "international_box_office", "opening_weekend", "rationale"] }
                },
                required: ["conservative", "realistic", "breakout"]
            },
            greenlight_analysis: {
                type: "OBJECT",
                properties: {
                    recommended_budget_cap: { type: "NUMBER" },
                    profitability_verdict: { type: "STRING" },
                    break_even_point: { type: "NUMBER" }
                },
                required: ["recommended_budget_cap", "profitability_verdict", "break_even_point"]
            },
            recoupment_structure: { type: "OBJECT", properties: { exhibitor_share_percent: { type: "NUMBER" }, distribution_fee_percent: { type: "NUMBER" }, note: { type: "STRING" } }, required: ["exhibitor_share_percent", "distribution_fee_percent"] },
            ancillary_revenue_multipliers: { type: "OBJECT", properties: { conservative: { type: "NUMBER" }, realistic: { type: "NUMBER" }, breakout: { type: "NUMBER" }, explanation: { type: "STRING" } }, required: ["conservative", "realistic", "breakout"] },
            comparable_films: { type: "ARRAY", items: { type: "OBJECT", properties: { title: { type: "STRING" }, box_office: { type: "STRING" }, scenario_match: { type: "STRING" } }, required: ["title", "box_office", "scenario_match"] } }
        },
        required: ["scenarios", "greenlight_analysis", "recoupment_structure", "ancillary_revenue_multipliers", "comparable_films"]
    };

    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [], GEMINI_2_5_PRO_MODEL);
};

export const generateDetailedStreamingForecastLLM = async (filmData, goal, comps, platforms) => {
    const prompt = `
    Based on the film data entered on the audience analysis, number of theaters (if any), cast, genre, etc., and based on your expertise as an experienced film distribution expert and consultant you will provide financial estimates for the following streaming & broadcast licensing categories.

    FILM DATA:
    - Title: "${filmData.title}"
    - Genre: "${filmData.genre}"
    - Cast/Stars: "${filmData.stars}"
    - Logline: "${filmData.logline}"
    
    STRATEGIC INPUTS:
    - Primary Goal: "${goal}"
    - Comparable Titles: "${comps}"
    - Target Platforms: "${platforms || 'Any suitable'}"

    Provide a valid JSON response. For each category (SVOD, AVOD, Linear TV), provide a rationale for your financial estimate based on the film's data and comparable titles.
    `;

    const forecastSchema = {
        type: "OBJECT",
        properties: {
            platforms: { type: "ARRAY", items: { type: "STRING" }, description: "Example platforms for this category." },
            estimated_revenue: { type: "STRING", description: "A financial forecast range for this category, e.g., '$500K - $1.5M'." },
            rationale: { type: "STRING", description: "A brief, data-driven rationale for the forecast." }
        },
        required: ["platforms", "estimated_revenue", "rationale"]
    };

    const schema = {
        type: "OBJECT",
        properties: {
            svod_forecast: forecastSchema,
            avod_forecast: forecastSchema,
            linear_tv_forecast: forecastSchema,
        },
        required: ["svod_forecast", "avod_forecast", "linear_tv_forecast"]
    };

    const model = GEMINI_2_5_PRO_MODEL;
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [], model);
};

export const generateVisualAnalysisLLM = async (filmData, posterA_base64, posterB_base64) => {
    const prompt = `
    As a film marketing expert, analyze these two movie poster versions for the film titled "${filmData.title}", which is in the "${filmData.genre}" genre. Your analysis must be strategic, considering which poster best communicates the film's tone and appeals to the target audience for this specific genre.

    CRITICAL INSTRUCTIONS:
    - Image A is the first image provided. Image B is the second.
    - Do NOT favor Version A by default. Evaluate both versions objectively and independently.
    - Base your recommendation SOLELY on marketing effectiveness, target audience appeal, and strategic fit for the ${filmData.genre} genre.
    - If Version B is stronger, you MUST recommend Version B. Do not default to Version A.
    - Your recommendation must be justified by concrete visual and strategic analysis, not by the order images were presented.

    Your output must be a single, valid JSON object with four keys:
    - "recommendation": Choose one: "Version A" or "Version B" - whichever is ACTUALLY stronger for marketing this film.
    - "confidence": A number between 0 and 100 representing your confidence in the recommendation. Be critical; a score of 95+ should be rare and reserved for exceptionally clear-cut choices.
    - "insight": A concise, executive-level paragraph explaining the strategic reasoning behind your choice, referencing the film's genre and title. Focus on WHY this version works better for the target audience.
    - "comparisonPoints": A bulleted list of 3-5 direct comparison points (e.g., "Color Palette: Version A uses warm tones that convey intimacy vs Version B's cool palette suggesting isolation", "Typography: Version A's bold serif commands attention vs Version B's minimalist sans-serif", "Character Posing: Version A shows confrontation vs Version B implies mystery").
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            recommendation: { type: "STRING", enum: ["Version A", "Version B"] },
            confidence: { type: "NUMBER" },
            insight: { type: "STRING" },
            comparisonPoints: { type: "ARRAY", items: { type: "STRING" } },
        },
        required: ["recommendation", "confidence", "insight", "comparisonPoints"]
    };

    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [posterA_base64, posterB_base64]);
};


export const generateSocialGraphicsLLM = async (filmTitle, genre, insight, recommendedPosterBase64) => {
    const graphicsToGenerate = [
        { type: "Instagram Story" },
        { type: "Character Quote Card" },
        { type: "Facebook Banner" },
        { type: "YouTube Thumbnail" }
    ];

    const imagePromises = graphicsToGenerate.map(graphic => {
        const prompt = `Generate a ${graphic.type} for the ${genre} film "${filmTitle}". The key marketing insight is: "${insight}". Use the visual style of the provided reference movie poster.`;
        
        return callImagenApi(prompt)
            .then(imageUrl => ({ title: graphic.type, url: imageUrl }))
            .catch(error => {
                console.error(`Error generating ${graphic.type}:`, error);
                return {
                    title: graphic.type,
                    url: `https://placehold.co/500x500/18181b/ff0000?text=Error`,
                    error: true
                };
            });
    });

    return Promise.all(imagePromises);
};


// --- REVISED & COMPLETE: generateDetailedVideoAnalysisLLM ---
export const generateDetailedVideoAnalysisLLM = async (frames, genre, filmTitle) => {
  const prompt = `
You are a world-class film trailer analyst and marketing strategist. Analyze these keyframes from a ${genre} trailer titled "${filmTitle}" with the precision of a seasoned acquisitions executive combined with the platform instincts of a viral content director.

=== BUZZ FACTOR CRITERIA ===
- "Low" (0–39): Generic footage, weak hooks, poor pacing, unclear story, forgettable imagery
- "Medium" (40–64): Solid but predictable, some engaging moments, standard genre execution
- "High" (65–84): Strong hooks, compelling visuals, clear emotional beats, memorable money shots
- "Viral Potential" (85–100): Shocking/surprising elements, meme-worthy content, highly shareable moments, unique concept that demands a reaction

=== PACING ANALYSIS ===
Evaluate pacing grade as: "A" (perfect rhythm, builds masterfully), "B" (mostly effective, minor lulls), "C" (uneven, some dead spots), "D" (poor rhythm, kills momentum), "F" (chaotic or flatline).

=== PLATFORM FIT ===
Score each platform 0–100 for trailer fit based on content style, length viability, audience match, and visual language:
- YouTube (long-form trailer home, broad audience)
- Instagram (visual polish, 30s sweet spot, lifestyle appeal)
- TikTok (first-3-second hook, trend potential, sound-off watchability)
- Facebook (older demo, emotional storytelling, community share potential)
- Twitter/X (discourse-driving, controversy/hype potential, gif-worthy moments)

=== WEAKNESSES ===
Be brutally honest. Identify 3–5 specific weaknesses. For each, assign a severity: "Critical" (kills buzz), "Moderate" (hurts performance), or "Minor" (polish issue).

=== HIGH BUZZ ROADMAP ===
Provide 4–6 specific, actionable recommendations to elevate this trailer from its current buzz level to "Viral Potential". These should be concrete editorial, marketing, or creative suggestions — not generic advice.

Your entire response MUST be a single, valid JSON object.

YOUR JSON OUTPUT MUST CONFORM TO THIS EXACT STRUCTURE:
{
  "OverallImpression": "A concise, top-level summary of the trailer's effectiveness as a marketing asset.",
  "CoreConcept": "A punchy one-phrase concept (e.g., 'A gritty sci-fi survival thriller with a redemption arc').",
  "PredictedBuzzFactor": "Choose one: 'Low', 'Medium', 'High', or 'Viral Potential'",
  "PredictedBuzzScore": 72,
  "KeyStrengths": ["2–4 specific, potent marketing strengths as strings."],
  "Weaknesses": [
    { "issue": "Short label for the weakness", "detail": "Specific description of the problem.", "severity": "Critical | Moderate | Minor" }
  ],
  "HighBuzzRoadmap": [
    { "action": "Short action title", "detail": "Specific, concrete recommendation to increase buzz and shareability." }
  ],
  "PacingAssessment": {
    "grade": "A | B | C | D | F",
    "notes": "Specific pacing observations — where it works, where it dies."
  },
  "PlatformFitScores": {
    "YouTube": 80,
    "Instagram": 65,
    "TikTok": 45,
    "Facebook": 70,
    "TwitterX": 55
  },
  "PrimaryAudience": "A detailed profile of the ideal viewer — demographics, psychographics, viewing habits.",
  "SecondaryAudiences": ["Other potential viewer segments as strings."],
  "MoneyShot": "Description of the single most memorable and marketable frame in the trailer.",
  "CompetitiveComps": ["2–3 comparable trailers or films for benchmark context (e.g., 'Get Out (2017) trailer')."],
  "SuggestedTaglines": ["3–5 potential marketing taglines as strings."],
  "SpotBreakdowns": {
    "spot60": {
      "goal": "Strategic objective of the 60s TV spot.",
      "shots": [
        { "timecode": "00:00–00:04", "description": "Opening shot description. Each shot object MUST include a timecode range (HH:MM–HH:SS) and a description." }
      ]
    },
    "spot30": {
      "goal": "Strategic objective of the 30s TV spot.",
      "shots": [
        { "timecode": "00:00–00:03", "description": "Opening shot description with timecode range." }
      ]
    },
    "spot15": {
      "goal": "Strategic objective of the 15s social spot.",
      "shots": [
        { "timecode": "00:00–00:02", "description": "Opening shot description with timecode range." }
      ]
    }
  }

CRITICAL RULE FOR SpotBreakdowns: Every single shot in every spot MUST be an object with exactly two fields:
- "timecode": A precise in/out range string (e.g. "00:04–00:08") that fits within the spot duration. Times must be sequential, non-overlapping, and sum to approximately the spot length.
- "description": The shot description, camera direction, and editorial intent.
NEVER output shots as bare strings. ALWAYS output them as { "timecode": "...", "description": "..." } objects.
}
`;

  const schema = {
    type: "OBJECT",
    properties: {
      OverallImpression: { type: "STRING" },
      CoreConcept: { type: "STRING" },
      PredictedBuzzFactor: { 
        type: "STRING", 
        enum: ["Low", "Medium", "High", "Viral Potential"]
      },
      PredictedBuzzScore: { type: "NUMBER" },
      KeyStrengths: { type: "ARRAY", items: { type: "STRING" } },
      Weaknesses: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            issue: { type: "STRING" },
            detail: { type: "STRING" },
            severity: { type: "STRING", enum: ["Critical", "Moderate", "Minor"] }
          },
          required: ["issue", "detail", "severity"]
        }
      },
      HighBuzzRoadmap: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            action: { type: "STRING" },
            detail: { type: "STRING" }
          },
          required: ["action", "detail"]
        }
      },
      PacingAssessment: {
        type: "OBJECT",
        properties: {
          grade: { type: "STRING", enum: ["A", "B", "C", "D", "F"] },
          notes: { type: "STRING" }
        },
        required: ["grade", "notes"]
      },
      PlatformFitScores: {
        type: "OBJECT",
        properties: {
          YouTube: { type: "NUMBER" },
          Instagram: { type: "NUMBER" },
          TikTok: { type: "NUMBER" },
          Facebook: { type: "NUMBER" },
          TwitterX: { type: "NUMBER" }
        },
        required: ["YouTube", "Instagram", "TikTok", "Facebook", "TwitterX"]
      },
      PrimaryAudience: { type: "STRING" },
      SecondaryAudiences: { type: "ARRAY", items: { type: "STRING" } },
      MoneyShot: { type: "STRING" },
      CompetitiveComps: { type: "ARRAY", items: { type: "STRING" } },
      SuggestedTaglines: { type: "ARRAY", items: { type: "STRING" } },
      SpotBreakdowns: {
        type: "OBJECT",
        properties: {
          spot60: { 
            type: "OBJECT", 
            properties: { 
              goal: { type: "STRING" }, 
              shots: { 
                type: "ARRAY", 
                items: { 
                  type: "OBJECT", 
                  properties: { 
                    timecode: { type: "STRING" }, 
                    description: { type: "STRING" } 
                  }, 
                  required: ["timecode", "description"] 
                } 
              } 
            }, 
            required: ["goal", "shots"] 
          },
          spot30: { 
            type: "OBJECT", 
            properties: { 
              goal: { type: "STRING" }, 
              shots: { 
                type: "ARRAY", 
                items: { 
                  type: "OBJECT", 
                  properties: { 
                    timecode: { type: "STRING" }, 
                    description: { type: "STRING" } 
                  }, 
                  required: ["timecode", "description"] 
                } 
              } 
            }, 
            required: ["goal", "shots"] 
          },
          spot15: { 
            type: "OBJECT", 
            properties: { 
              goal: { type: "STRING" }, 
              shots: { 
                type: "ARRAY", 
                items: { 
                  type: "OBJECT", 
                  properties: { 
                    timecode: { type: "STRING" }, 
                    description: { type: "STRING" } 
                  }, 
                  required: ["timecode", "description"] 
                } 
              } 
            }, 
            required: ["goal", "shots"] 
          }
        },
        required: ["spot60", "spot30", "spot15"]
      }
    },
    required: [
      "OverallImpression", "CoreConcept", "PredictedBuzzFactor", "PredictedBuzzScore",
      "KeyStrengths", "Weaknesses", "HighBuzzRoadmap", "PacingAssessment",
      "PlatformFitScores", "PrimaryAudience", "SecondaryAudiences",
      "MoneyShot", "CompetitiveComps", "SuggestedTaglines", "SpotBreakdowns"
    ]
  };

  return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, frames);
};

export const generateCampaignConceptsLLM = async (filmData, audienceAnalysis) => {
        // BATCH FIX: The prompt is now more direct and commanding to prevent the AI from generating conversational text.
        // It emphasizes the required JSON output and removes advisory language.
        const prompt = `
        You are an advertising A/B test generation machine. Your ONLY task is to return a valid JSON array containing exactly three distinct campaign concept objects for the film "${filmData.title}".
    
        **FILM & AUDIENCE DATA:**
        - **Primary Target:** "${audienceAnalysis.target_audience_breakdown?.primary_audience_profile}"
        - **Psychographics:** "${audienceAnalysis.target_audience_breakdown?.psychographics?.join(', ')}"
        - **Key Themes:** "${filmData.themes}"
    
        **JSON OUTPUT STRUCTURE:**
        Provide a JSON array with exactly three objects. Each object must have the following keys: "conceptName", "targetSegment", "hypothesis", "ad_a", "ad_b".
        The "ad_a" and "ad_b" values must be objects containing "headline", "body", and "image_prompt".
    
        **EXAMPLE OF ONE OBJECT IN THE ARRAY:**
        {
          "conceptName": "Emotional Core vs. High-Stakes Action",
          "targetSegment": "Focus on viewers who enjoy character-driven dramas.",
          "hypothesis": "Test whether emotional messaging resonates more than action-oriented messaging.",
          "ad_a": {
            "headline": "Her Fight Was For More Than Her Life",
            "body": "Discover the story of a woman who risked everything for justice.",
            "image_prompt": "Cinematic, dramatic closeup of the lead actress, face showing intense emotion, tears in her eyes, set against a dark, moody background."
          },
          "ad_b": {
            "headline": "The System is Broken. She Will Be The Hammer.",
            "body": "One woman's battle against a corrupt system. The odds are stacked against her, but she's not backing down.",
            "image_prompt": "Dynamic action shot of the lead actress in a tense courtroom standoff, pointing an accusatory finger. High contrast lighting."
          }
        }
    
        **DO NOT write any text, explanation, or introduction. Your entire response must be ONLY the valid JSON array.**
        `;
        const schema = {
            type: "ARRAY",
            items: {
                type: "OBJECT",
                properties: {
                    conceptName: { type: "STRING" },
                    targetSegment: { type: "STRING" },
                    hypothesis: { type: "STRING" },
                    ad_a: {
                        type: "OBJECT",
                        properties: { headline: { type: "STRING" }, body: { type: "STRING" }, image_prompt: { type: "STRING" }, },
                        required: ["headline", "body", "image_prompt"]
                    },
                    ad_b: {
                        type: "OBJECT",
                        properties: { headline: { type: "STRING" }, body: { type: "STRING" }, image_prompt: { type: "STRING" }, },
                        required: ["headline", "body", "image_prompt"]
                    }
                },
                required: ["conceptName", "targetSegment", "hypothesis", "ad_a", "ad_b"]
            }
        };
        // BATCH FIX: Add a token limit to prevent overly verbose, unparsable responses.
        const config = { 
            responseMimeType: "application/json", 
            responseSchema: schema,
            maxOutputTokens: 8192 
        };
        return await callGeminiApi(prompt, config);
    };
    
// LLM for Content Creation Studio
// Gaps 2, 3, 4 — asset-allocation-weighted format counts, video intelligence injection, winning hook bias
export const generateAdConceptsLLM = async (filmData, audienceAnalysis, videoAnalysis = null) => {
    // 1. Creative Matrix hooks
    const matrix = audienceAnalysis?.creative_matrix || [];
    const matrixContext = matrix.length > 0 
        ? matrix.map((m, i) => `Hook 0${i+1} [${m.hook_name}]: Trigger=${m.psychological_trigger}, Visuals=${m.visual_direction}, Copy Tone=${m.copy_angle}`).join('\n')
        : "Standard genre conventions apply.";

    // 2. Gap 2 — Derive exact format counts from asset_allocation percentages
    // Total batch = 6 concepts. Andromeda's allocation drives exactly how many of each format we generate.
    const allocation = audienceAnalysis?.asset_allocation || { vertical_video_percent: 60, carousel_percent: 30, static_image_percent: 10 };
    const TOTAL = 6;
    const verticalCount = Math.max(1, Math.round((allocation.vertical_video_percent / 100) * TOTAL));
    const carouselCount = Math.max(1, Math.round((allocation.carousel_percent / 100) * TOTAL));
    const staticCount   = Math.max(0, TOTAL - verticalCount - carouselCount);

    // 3. Gap 3 — Inject video analysis as creative raw material
    const videoContext = videoAnalysis ? `
TRAILER INTELLIGENCE (from Video Analysis — use this as creative raw material):
- Money Shot: "${videoAnalysis.MoneyShot || 'N/A'}"
- Suggested Taglines: ${(videoAnalysis.SuggestedTaglines || []).map(t => `"${t}"`).join(', ') || 'N/A'}
- Strongest Platform: ${Object.entries(videoAnalysis.PlatformFitScores || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
- Trailer Primary Audience: "${videoAnalysis.PrimaryAudience || 'N/A'}"
- Core Concept: "${videoAnalysis.CoreConcept || 'N/A'}"
INSTRUCTION: Directly incorporate the Money Shot description and at least one Suggested Tagline into your concepts where relevant.` 
    : '';

    // 4. Gap 4 — Bias toward hooks proven to win in live campaigns
    const winningHooks = filmData.winning_hooks || [];
    const winningHookContext = winningHooks.length > 0
        ? `\nPROVEN WINNING HOOKS (bias toward these in new formats):\n${winningHooks.map(h => `- "${h}"`).join('\n')}`
        : '';

    const prompt = `
    You are an elite advertising creative director for the film industry, optimizing for algorithmic social platforms (TikTok, Reels, Meta).
    Based on the film's details and the pre-approved "Creative Matrix" below, generate exactly ${TOTAL} distinct ad concepts.
    
    FILM DATA:
    - Title: "${filmData.title}"
    - Genre: "${filmData.genre}"
    - Logline: "${filmData.logline}"
    - Key Themes: "${filmData.themes || 'Not specified'}"

    APPROVED CREATIVE MATRIX (You MUST base your concepts on these exact hooks):
    ${matrixContext}
    ${winningHookContext}
    ${videoContext}

    MANDATORY FORMAT DISTRIBUTION — Your ${TOTAL} concepts MUST use exactly these counts:
    - Vertical Video: ${verticalCount} concept(s)  [${allocation.vertical_video_percent}% Andromeda allocation — highest priority]
    - Carousel: ${carouselCount} concept(s)         [${allocation.carousel_percent}% Andromeda allocation]
    - Static Image: ${staticCount} concept(s)       [${allocation.static_image_percent}% Andromeda allocation]
    This is not a suggestion. It is the Andromeda-recommended asset mix. Every concept must declare its format.
    
    INSTRUCTIONS:
    1. Create exactly ${TOTAL} unique visual concepts matching the format counts above.
    2. Each concept MUST align directly with one of the hooks from the Creative Matrix.
    3. If winning hooks exist above, ensure at least one concept executes that hook in a new format.
    
    CRITICAL IMAGE PROMPT RULE:
    - The "image_prompt" is for an AI image generator (Imagen). IT MUST BE "SAFE FOR WORK" (PG-13).
    - DO NOT use words like "blood", "gore", "open wound", "violence", "kill", or "dead body".
    - Instead, use atmospheric metaphors: "red cinematic lighting", "ominous shadow", "intense atmosphere", "high contrast thriller aesthetic".

    Return a valid JSON array of exactly ${TOTAL} objects.
    `;
    
    const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image"], description: "The intended delivery format." },
                aligned_hook: { type: "STRING", description: "The name of the hook from the Creative Matrix this concept serves." },
                headline: { type: "STRING", description: "The hook text or on-screen graphic." },
                body: { type: "STRING", description: "The caption or script voiceover." },
                image_prompt: { type: "STRING", description: "PG-13 visual prompt for AI generator." },
                strategy_blurb: { type: "STRING", description: "Why this specifically works for the algorithm." }
            },
            required: ["format", "aligned_hook", "headline", "body", "image_prompt", "strategy_blurb"]
        }
    };
    
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [], GEMINI_2_5_PRO_MODEL);
};

export const generateBioLLM = async (name, role, filmTitle) => {
    const prompt = `You are a professional publicist. Write a compelling, one-paragraph biography (50-70 words) for a press kit.
    
    - Name: ${name}
    - Role: ${role}
    - Film: ${filmTitle}
    
    The bio should be professional and highlight their contribution to the film. Do not use markdown.`;
    return callGeminiApi(prompt);
};

export const generateDetailedCompetitorAnalysisLLM = async (competitorTitle, yourFilmData) => {
    const prompt = `
    You are a ruthless Film Marketing Strategist. Perform a forensic "Vs." analysis between two films.
    
    HERO PROJECT (Us): "${yourFilmData.title}" (${yourFilmData.genre})
    Logline: ${yourFilmData.logline}
    Budget: ${yourFilmData.budgetLevel}
    
    TARGET PROJECT (Them): "${competitorTitle}"
    
    TASK: Deconstruct the Target Project's performance to find strategic advantages for the Hero Project.
    
    OUTPUT: A valid JSON object with this exact schema:
    {
        "tale_of_the_tape": {
            "box_office_verdict": "Hit, Flop, or Sleeper?",
            "budget_estimated": "Est. Budget string (e.g. '$15M')",
            "opening_weekend": "Est. Opening string",
            "critic_score": "Est. Rotten Tomatoes score (e.g. '85%')",
            "audience_score": "Est. Audience score (e.g. '92%')"
        },
        "forensic_analysis": {
            "the_kill_shot": "The SINGLE marketing move or hook that defined their success (or failure). Be specific (e.g. 'The viral TikTok dance', 'The misleading trailer tone').",
            "audience_overlap": "High, Medium, or Low",
            "overlap_explanation": "Why our audiences overlap (or don't).",
            "missing_ingredient": "What did they lack that WE have? (e.g. 'Heart', 'Better VFX', 'Modern social commentary')."
        },
        "swot_comparison": {
            "their_strength": "What they did perfectly.",
            "their_weakness": "Where they dropped the ball.",
            "our_opportunity": "How we capitalize on their weakness.",
            "our_threat": "What they did that we must match or beat."
        },
        "battle_plan": {
            "steal_this": "One specific tactic to copy.",
            "exploit_this": "One specific gap to target.",
            "ignore_this": "One distraction to ignore."
        }
    }
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            tale_of_the_tape: {
                type: "OBJECT",
                properties: { box_office_verdict: {type:"STRING"}, budget_estimated: {type:"STRING"}, opening_weekend: {type:"STRING"}, critic_score: {type:"STRING"}, audience_score: {type:"STRING"} },
                required: ["box_office_verdict", "budget_estimated"]
            },
            forensic_analysis: {
                type: "OBJECT",
                properties: { the_kill_shot: {type:"STRING"}, audience_overlap: {type:"STRING"}, overlap_explanation: {type:"STRING"}, missing_ingredient: {type:"STRING"} },
                required: ["the_kill_shot", "audience_overlap", "missing_ingredient"]
            },
            swot_comparison: {
                type: "OBJECT",
                properties: { their_strength: {type:"STRING"}, their_weakness: {type:"STRING"}, our_opportunity: {type:"STRING"}, our_threat: {type:"STRING"} },
                required: ["their_strength", "their_weakness", "our_opportunity"]
            },
            battle_plan: {
                type: "OBJECT",
                properties: { steal_this: {type:"STRING"}, exploit_this: {type:"STRING"}, ignore_this: {type:"STRING"} },
                required: ["steal_this", "exploit_this", "ignore_this"]
            }
        },
        required: ["tale_of_the_tape", "forensic_analysis", "swot_comparison", "battle_plan"]
    };

    const config = { responseMimeType: "application/json", responseSchema: schema };
    // Use your existing queue/model constants
    return await callGeminiApi(prompt, config, [], GEMINI_2_5_PRO_MODEL);
};

export const analyzeSocialScreenshotLLM = async (base64Image) => {
    const prompt = `
    Analyze this screenshot of a social media comment/post. 
    Extract the following data in JSON format:
    1. "commenterName": The username (or "Anonymous" if not visible).
    2. "commentText": The exact text of the post/comment.
    3. "platform": The likely platform (Instagram, Twitter, Letterboxd, etc).
    4. "sentiment": "Positive", "Neutral", or "Negative".
    5. "keywords": Array of 3 keywords describing the praise.
    
    Only return valid JSON.
    `;

    // FIX: Removed hardcoded "gemini-1.5-flash". Now uses the global GEMINI_FLASH_MODEL constant.
    return await callGeminiApi(prompt, { responseMimeType: "application/json" }, [base64Image], GEMINI_FLASH_MODEL);
};

// --- REMIX STRATEGY ---
export const generateRemixContentLLM = async (filmData, feedbackItems) => {
    const quotes = feedbackItems.map(f => `"${f.commentText}" - ${f.commenterName}`).join("\n");
    const prompt = `
    You are a social media manager for the film "${filmData.title}". 
    Audience reactions:
    ${quotes}

    Generate a "Social Proof Campaign" in JSON format with:
    1. "caption_instagram": Catchy caption.
    2. "caption_twitter": Punchy tweet.
    3. "visual_prompt": A prompt for an AI image generator to create a textured, cinematic background (text-free, atmospheric, matching ${filmData.genre}).
    
    CRITICAL: The visual_prompt must be SAFE FOR WORK. No blood, gore, or violence. Use metaphors (e.g. "red lighting", "ominous shadows").
    `;

    return await callGeminiApi(prompt, { responseMimeType: "application/json" });
};

export const generateBudgetPlanLLM = async (totalBudget, filmData, audienceAnalysis) => {
    const prompt = `
    You are a senior film-marketing strategist and media-buying analyst. Build a top-quality marketing-budget allocation plan for the independent feature described below. The plan must be data-driven, reference the film’s specific genre, cast, cultural hooks, and release strategy, and avoid boilerplate percentages.

    INPUT FILM DETAILS:
    • Title: "${filmData.title}"
    • Logline: "${filmData.logline}"
    • Genre / Tone: "${filmData.genre}"
    • Budget Level (production): "${filmData.budgetLevel}"
    • Cast & Fan Bases: "${filmData.stars || 'Not specified'}"
    • Key Themes / Cultural Hooks: "${filmData.themes}"
    • Intended Release Path: "Limited Theatrical -> VOD"
    • Total Marketing Budget (USD): $${Number(totalBudget).toLocaleString()}
    • Target Window: "${filmData.releaseWindow || 'Not specified'}"

    DELIVER THE RESPONSE AS A VALID JSON OBJECT.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            executive_snapshot: {
                type: "OBJECT",
                properties: {
                    rationale: { type: "STRING", description: "One-paragraph rationale for the budget mix." },
                    kpis: { type: "ARRAY", items: { type: "STRING" }, description: "Top 3 success metrics (KPIs)." }
                },
                required: ["rationale", "kpis"]
            },
            budget_allocation_table: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        bucket: { type: "STRING" },
                        percent_of_budget: { type: "NUMBER" },
                        dollar_amount: { type: "NUMBER" },
                        notes: { type: "STRING" }
                    },
                    required: ["bucket", "percent_of_budget", "dollar_amount", "notes"]
                }
            },
            activity_playbook: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        bucket: { type: "STRING" },
                        tactics: { type: "ARRAY", items: { type: "STRING" } }
                    },
                    required: ["bucket", "tactics"]
                }
            },
            phased_timeline: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        phase: { type: "STRING", description: "e.g., 'Pre-Launch (Weeks -8 to -4)'" },
                        activities: { type: "ARRAY", items: { type: "STRING" } }
                    },
                    required: ["phase", "activities"]
                }
            },
            risk_and_flex_budget: {
                type: "OBJECT",
                properties: {
                    flex_lines: { type: "STRING", description: "Identify two cost lines that can flex up/down." },
                    trigger_kpis: { type: "STRING", description: "Propose trigger KPIs that release contingency funds." }
                },
                required: ["flex_lines", "trigger_kpis"]
            }
        },
        required: ["executive_snapshot", "budget_allocation_table", "activity_playbook", "phased_timeline", "risk_and_flex_budget"]
    };

    const model = GEMINI_2_5_PRO_MODEL;
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema }, [], model);
};

export const generatePitchAnglesLLM = async (filmData, audienceAnalysis) => {
    const prompt = `
    You are a savvy publicist AI for the film industry. For the film "${filmData.title}", generate 5 distinct and compelling pitch angles for journalists. Each angle should be tailored for a different type of publication or media outlet (e.g., tech blog, lifestyle magazine, film trade publication, cultural journal, local news).
    
    KEY FILM DATA:
    - Logline: ${filmData.logline}
    - Genre: ${filmData.genre}
    - Key Themes: ${filmData.themes}
    - Audience Insights: ${audienceAnalysis?.executive_summary?.join(' ')}

    Return a valid JSON array of objects. Each object must have a "publication_type" (e.g., "Tech Blog") and an "angle" (the pitch idea).
    `;
    
    const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                publication_type: { type: "STRING" },
                angle: { type: "STRING" }
            },
            required: ["publication_type", "angle"]
        }
    };
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};

export const generatePitchEmailLLM = async (filmData, contact, pitchAngle) => {
    const prompt = `
    You are an expert publicist AI. Your task is to draft a personalized pitch email to a journalist about the film "${filmData.title}".

    EMAIL RECIPIENT:
    - Name: ${contact.name}
    - Outlet: ${contact.outlet}

    FILM DETAILS:
    - Logline: ${filmData.logline}
    - Key Themes: ${filmData.themes}

    PITCH ANGLE TO USE:
    - Angle: "${pitchAngle.angle}"
    - For Publication Type: "${pitchAngle.publication_type}"

    Draft a concise, compelling, and professional email. The output should be a single block of text representing the email body. Start with a greeting (e.g., "Hi ${contact.name},") and end with a professional closing.
    `;
    return callGeminiApi(prompt);
};

export const generateBlueprintSection = async (sectionName, allData, model = GEMINI_2_5_PRO_MODEL) => {
const { filmData, videoAnalysis, budgetPlan } = allData;
    const audienceAnalysis = filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;

    // A lean, sanitized data object for the prompt, now including release strategy
    const sanitizedData = {
        title: filmData.title,
        logline: filmData.logline,
        genre: filmData.genre,
        themes: filmData.themes,
        audience: {
            primary: audienceAnalysis?.target_audience_breakdown?.primary_audience_profile,
            psychographics: audienceAnalysis?.target_audience_breakdown?.psychographics,
        },
        video_buzz_factor: videoAnalysis?.PredictedBuzzFactor,
        budget_total: budgetPlan?.budget_allocation_table?.reduce((sum, item) => sum + item.dollar_amount, 0),
        budget_kpis: budgetPlan?.executive_snapshot?.kpis,
        optimal_release_strategy: filmData.script_analysis?.optimal_release_strategy,
    };

    const prompt = `
    You are a top-tier film marketing executive presenting to a major studio. Your task is to write ONE section of a larger Marketing & PR Blueprint: "${sectionName}".
    The tone must be confident, strategic, direct, and authoritative. Avoid generic marketing language. Use active, powerful verbs.

    Base your analysis on this data: ${JSON.stringify(sanitizedData)}

    ${sectionName === 'Executive Summary' ? "IMPORTANT: You MUST incorporate the provided 'optimal_release_strategy' information directly into your 'strategic_analysis' paragraph for this section." : ""}

    Your output must be a single, valid JSON object with two keys:
    - "strategic_analysis": A concise, executive-level paragraph analyzing what the data means for this specific section.
    - "action_items": A bulleted list of 3-5 concrete, actionable directives for this section.
    `;

    const schema = {
        type: "OBJECT",
        properties: {
            strategic_analysis: { type: "STRING" },
            action_items: { type: "ARRAY", items: { type: "STRING" } }
        },
        required: ["strategic_analysis", "action_items"]
    };
    
    const config = { responseMimeType: "application/json", responseSchema: schema, maxOutputTokens: 2048 };
    return await callGeminiApi(prompt, config, [], model);
};

export const generatePerformanceSummaryLLM = async (filmData, budgetPlan, videoAnalysis, revenueForecast) => {
    // FIX: Sanitize filmData to remove large base64 fields before stringifying
    const { poster_a, poster_b, video_keyframes_urls, ...sanitizedFilmData } = filmData;

    const prompt = `
    You are BLACKBOX, a senior film marketing analyst AI. Your task is to provide a top-level executive summary of a film's marketing strategy health. Synthesize the provided data into a concise, actionable report. Use Markdown for formatting.

    INPUT DATA:
    - Film Details: ${JSON.stringify(sanitizedFilmData)}
    - Budget Plan: ${budgetPlan ? JSON.stringify(budgetPlan) : "Not available"}
    - Video Analysis Buzz Factor: ${videoAnalysis?.PredictedBuzzFactor || "Not available"}
    - Revenue Forecast: ${revenueForecast ? JSON.stringify(revenueForecast) : "Not available"}
    
    OUTPUT: A markdown-formatted string with the following sections, using bolding for headers:
    - **Overall Health**: A one-sentence summary (e.g., "Strong foundation with high buzz potential, but budget may be tight.").
    - **Key Strengths**: A bulleted list of 2-3 major strengths.
    - **Potential Red Flags**: A bulleted list of 2-3 areas to watch.
    - **Strategic Recommendation**: A single, actionable recommendation.
    `;
    return await callGeminiApi(prompt);
};

export const generateInfluencerListLLM = async (filmData, audienceAnalysis) => {
    const prompt = `
    As an expert talent manager and digital marketing AI specializing in influencer partnerships for films, analyze the following film and audience data.
    
    FILM:
    - Title: "${filmData.title}"
    - Genre: "${filmData.genre}"
    - Key Themes: "${filmData.themes}"

    AUDIENCE:
    - Primary Audience: "${audienceAnalysis.target_audience_breakdown.primary_audience_profile}"
    - Psychographics: ${audienceAnalysis.target_audience_breakdown.psychographics.join(', ')}

    Your task is to identify 5-7 real-world influencers across TikTok, YouTube, and Instagram who would be a perfect fit for a marketing campaign for this film.
    Return a valid JSON array of objects. Each object must contain:
    - "name": The influencer's real name or known alias.
    - "handle": Their exact social media handle.
    - "platform": "TikTok", "YouTube", or "Instagram".
    - "followers": A string representing their follower count (e.g., "1.2M", "250K").
    - "reasoning": A concise, one-sentence explanation for why they are a strategic fit.
    - "outreach_idea": A specific, creative idea for a collaboration or sponsored post.
    `;
    
    const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                name: { type: "STRING" },
                handle: { type: "STRING" },
                platform: { type: "STRING", enum: ["TikTok", "YouTube", "Instagram"] },
                followers: { type: "STRING" },
                reasoning: { type: "STRING" },
                outreach_idea: { type: "STRING" }
            },
            required: ["name", "handle", "platform", "followers", "reasoning", "outreach_idea"]
        }
    };
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};

export const generateAiReplySuggestionsLLM = async (mention, filmTitle, persona) => {
    const prompt = `
    You are an AI social media manager for the film "${filmTitle}". Your current persona is: "${persona}".
    A user has posted the following comment about the film:
    
    "${mention.content}"
    
    The comment has been classified with a sentiment of: ${mention.sentiment}.
    
    Your task is to generate three distinct, appropriate, and on-brand reply suggestions.
    Return a valid JSON array of strings. Each string is one reply suggestion.
    `;

    const schema = {
        type: "ARRAY",
        items: { type: "STRING" }
    };
    
    return await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
};

// Add these functions to your existing aiService.js file

/**
 * Generate social proof content from extracted feedback
 */
export const generateSocialProofContentLLM = async (feedbackArray, filmData) => {
    try {
        const prompt = `
Create compelling social media content using this positive audience feedback for "${filmData.title}":

FEEDBACK DATA:
${feedbackArray.map((fb, i) => `
${i + 1}. Platform: ${fb.platform}
   Commenter: ${fb.commenterName}
   Comment: "${fb.commentText}"
   Sentiment: ${fb.sentiment}
   Metrics: ${JSON.stringify(fb.metrics)}
`).join('\n')}

FILM INFO:
- Title: ${filmData.title}
- Genre: ${filmData.genre}
- Logline: ${filmData.logline}
- Director: ${filmData.director}

Create 3 different social media posts that transform this feedback into compelling social proof content:

1. TESTIMONIAL POST (Instagram Story/Feed)
2. REVIEW COMPILATION (Facebook/Twitter)
3. AUDIENCE LOVE POST (TikTok/Instagram Reel)

For each post, provide:
- Platform-optimized copy
- Suggested hashtags
- Post format/style
- Whether it needs a generated image (true/false)
- Image generation prompt (if needed)

Format as JSON:
{
  "posts": [
    {
      "title": "Post Title",
      "platform": "instagram", 
      "format": "story/feed/reel",
      "copy": "Full post text...",
      "hashtags": ["filmlife", "audiencereaction"],
      "generateImage": true,
      "imagePrompt": "Description for image generation",
      "style": "testimonial/compilation/reaction"
    }
  ],
  "strategy": {
    "overallMessage": "Main message",
    "targetAudience": "Who this appeals to",
    "bestTimes": "Suggested posting times"
  }
}

Make the copy authentic and engaging, incorporating actual quotes from the feedback.`;

        // Call your LLM service (replace with actual implementation)
        const response = await fetch('https://geminigateway-z6tvc67fca-uc.a.run.app', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        modelName: 'gemini-2.5-flash-preview-04-17',
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        generationConfig: { responseMimeType: 'application/json' }
    })
});
const result = await response.json();
return JSON.parse(result.candidates[0].content.parts[0].text);

    } catch (error) {
        console.error('Social proof content generation failed:', error);
        throw new Error('Failed to generate social content');
    }
};

/**
 * Extract feedback from social media screenshot
 */
export const extractFeedbackFromImageLLM = async (imageBase64) => {
    try {
        const prompt = `
Analyze this screenshot of social media feedback. Extract:

1. Commenter's name (if visible)
2. Complete comment text
3. Sentiment (positive/negative/neutral)
4. Platform (facebook/instagram/twitter/tiktok/youtube/etc)
5. Engagement metrics (likes, shares, replies if visible)
6. Context (is this a reply, review, post, etc.)

Return as JSON:
{
  "commenterName": "Name or 'Anonymous'",
  "commentText": "Exact text content",
  "sentiment": "positive/negative/neutral",
  "platform": "platform_name",
  "metrics": {
    "likes": 0,
    "shares": 0,
    "replies": 0
  },
  "context": "comment/review/post",
  "confidence": 0.95
}

If text is unclear or image quality is poor, return null.
Only return positive sentiment feedback for social proof purposes.`;

        const response = await fetch('https://geminigateway-z6tvc67fca-uc.a.run.app', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                modelName: 'gemini-2.5-flash-preview-04-17',
                contents: [{ role: 'user', parts: [
                    { inlineData: { mimeType: 'image/jpeg', data: imageBase64 } },
                    { text: prompt }
                ]}],
                generationConfig: { responseMimeType: 'application/json' }
            })
        });
        const result = await response.json();
        const parsed = JSON.parse(result.candidates[0].content.parts[0].text);

        // Only return positive feedback
        return parsed && parsed.sentiment === 'positive' ? parsed : null;

    } catch (error) {
        console.error('Feedback extraction failed:', error);
        return null;
    }
};

// --- SOCIAL MEDIA PLAYBOOK ENGINE ---
// Replaces generateProductionMarketingLLM for the Andromeda era.
// Generates a full pre-production-through-release social media campaign plan
// tuned to feed the algorithm creative variety across hooks, formats, and audience segments.
export const generateSocialMediaPlaybookLLM = async (filmData, audienceAnalysis, scriptContent) => {
    if (!scriptContent || scriptContent.length < 50) {
        throw new Error("Cannot generate Social Media Playbook: No script content detected.");
    }

    const scriptSnippet = scriptContent.substring(0, 18000);
    
    // Pre-extract character names to enforce a hard whitelist in the prompt.
    // The social playbook references characters in BTS moments and social angles —
    // this prevents the model from inventing names not present in the actual script.
    const socialCharNames = extractCharacterNames(scriptContent);

    // Pull Andromeda intelligence if available
    const matrix       = audienceAnalysis?.creative_matrix || [];
    const allocation   = audienceAnalysis?.asset_allocation || {};
    const primaryAud   = audienceAnalysis?.target_audience_breakdown?.primary_audience_profile || 'General audience';
    const secondaryAud = audienceAnalysis?.target_audience_breakdown?.secondary_audience_profile || '';
    const psychographics = (audienceAnalysis?.target_audience_breakdown?.psychographics || []).join(', ');
    const matrixContext = matrix.length > 0
        ? matrix.map((m, i) => `Hook ${i+1} [${m.hook_name}]: Trigger=${m.psychological_trigger}, Visuals=${m.visual_direction}, Copy=${m.copy_angle}`).join('\n')
        : 'Standard genre conventions.';

    const prompt = `
    You are an elite social media strategist for independent film, operating in the Andromeda algorithmic era.
    "Creative as Targeting" is the law — the algorithm finds the audience based on what the creative signals, not demographic parameters.
    Your job is to build a full social media campaign playbook from pre-production through opening weekend that maximizes Andromeda's ability to self-select the right audience at scale.

    FILM: "${filmData.title}" (${filmData.genre})
    LOGLINE: "${filmData.logline}"
    THEMES: "${filmData.themes || 'Not specified'}"
    BUDGET LEVEL: "${filmData.budgetLevel}"

    ANDROMEDA AUDIENCE INTELLIGENCE:
    - Primary Target: "${primaryAud}"
    - Secondary Target: "${secondaryAud}"
    - Psychographic Clusters: "${psychographics}"

    APPROVED CREATIVE MATRIX (every BTS asset and post must serve one of these hooks):
    ${matrixContext}

    ASSET ALLOCATION TARGET:
    - Vertical Video: ${allocation.vertical_video_percent || 60}%
    - Carousels: ${allocation.carousel_percent || 30}%
    - Static Images: ${allocation.static_image_percent || 10}%

    SCRIPT EXCERPT (use to identify actual scenes, characters, and moments for capture):
    """
    ${scriptSnippet}
    """

    ╔══════════════════════════════════════════════════════════════╗
    ║  CHARACTER NAME WHITELIST — HARD CONSTRAINT                  ║
    ║  These names were extracted directly from the script text.   ║
    ║  When referencing characters in BTS moments, social angles,  ║
    ║  or any other section — YOU MAY ONLY USE NAMES FROM THIS     ║
    ║  LIST. Any name not here does not exist in this script.      ║
    ║  Describe unnamed crew or extras by role, not invented name. ║
    ╚══════════════════════════════════════════════════════════════╝
    VALID CHARACTER NAMES: ${socialCharNames}

    CRITICAL INSTRUCTIONS:
    1. READ the script to identify real scenes, locations, and character moments — use only the character names from the whitelist above.
    2. Every capture item must specify: which Andromeda hook it serves, which format it should be posted as, whether it is organic or paid-to-promote, and which audience segment it is designed to find.
    3. 3. The BTS capture manifest must be highly targeted — generate exactly 8 to 10 high-value items — and must have VARIETY across: emotional registers (tension, humor, warmth, awe, conflict), subjects (cast, crew, locations, props, rehearsal, stunts, dailies reaction), and formats (vertical video clips, carousel sequences, static stills, text-overlay graphics).
    4. The paid promotion drops should be strategic — BTS moments that have the highest hook signal strength for Andromeda to amplify.
    5. The release timeline must cover: Pre-Production, Production (On-Set), Post-Production, Festival/Awards Run (if applicable), Release Week (Day-by-Day for opening weekend).
    9. The 90-day post-release plan must have distinct weekly/monthly content focus shifts — not a flat repeat of the pre-release strategy. Content should evolve from "awareness" to "social proof" to "long-tail discovery" as the film ages.
    10. The maintenance schedule must be behavior-triggered, not just calendar-based. Performance review triggers must reference actual Andromeda metrics, not vanity metrics like follower count.
    6. DO NOT include key art, poster photography, or PR/press contacts. This is a social-only playbook.
    7. Character social angles must specify which creative hook each character executes and what format amplifies them best.
    8. For the 90-day post-release plan and maintenance schedule, base content strategy on the film's actual themes and scenes, not on fabricated story beats.

    Return a single valid JSON object matching the schema exactly.
    `;

    const schema = {
        type: "OBJECT",
        properties: {

            // ── PHASE TIMELINE ──────────────────────────────────────────────
            release_timeline: {
                type: "ARRAY",
                description: "Full campaign arc from pre-production through opening weekend.",
                items: {
                    type: "OBJECT",
                    properties: {
                        phase: { type: "STRING", description: "e.g. 'Pre-Production (Weeks -16 to -12)'" },
                        strategic_goal: { type: "STRING", description: "What Andromeda outcome this phase is building toward." },
                        posts: {
                            type: "ARRAY",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    content_idea: { type: "STRING" },
                                    format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Story", "Text Overlay"] },
                                    andromeda_hook: { type: "STRING", description: "Which Creative Matrix hook this post executes." },
                                    audience_segment: { type: "STRING", description: "Which segment this is designed to find." },
                                    paid_promotion: { type: "BOOLEAN", description: "true = boost/promote this post with spend." },
                                    paid_rationale: { type: "STRING", description: "If paid, why this specific post warrants spend." },
                                    caption_direction: { type: "STRING", description: "Tone and copy direction — not the full caption." }
                                },
                                required: ["content_idea", "format", "andromeda_hook", "audience_segment", "paid_promotion", "caption_direction"]
                            }
                        }
                    },
                    required: ["phase", "strategic_goal", "posts"]
                }
            },

            // ── BTS CAPTURE MANIFEST ────────────────────────────────────────
            bts_capture_manifest: {
                type: "ARRAY",
                description: "Minimum 20 on-set capture directives spanning diverse hooks, formats, and emotional registers.",
                items: {
                    type: "OBJECT",
                    properties: {
                        moment: { type: "STRING", description: "Specific scene or setup to capture from the script." },
                        capture_type: { type: "STRING", enum: ["Vertical Video Clip", "Carousel Sequence", "Static Still", "Time-Lapse", "Talking Head", "Reaction Shot", "Text Overlay Card"] },
                        andromeda_hook: { type: "STRING", description: "Which Creative Matrix hook this moment serves." },
                        audience_segment: { type: "STRING", description: "Which Andromeda segment this will find." },
                        emotional_register: { type: "STRING", description: "e.g. Tension, Warmth, Humor, Awe, Conflict, Intimacy, Triumph" },
                        paid_drop: { type: "BOOLEAN", description: "Should this be a paid-to-promote post?" },
                        paid_drop_rationale: { type: "STRING", description: "Why this moment has high Andromeda signal strength worth buying." },
                        posting_phase: { type: "STRING", description: "Which campaign phase this belongs to." },
                        caption_direction: { type: "STRING" }
                    },
                    required: ["moment", "capture_type", "andromeda_hook", "audience_segment", "emotional_register", "paid_drop", "posting_phase", "caption_direction"]
                }
            },

            // ── CHARACTER SOCIAL ANGLES ─────────────────────────────────────
            character_social_angles: {
                type: "ARRAY",
                description: "Per-character social media angle tied to Andromeda hooks and optimal format.",
                items: {
                    type: "OBJECT",
                    properties: {
                        character_name: { type: "STRING" },
                        andromeda_hook_served: { type: "STRING", description: "Which Creative Matrix hook this character executes." },
                        audience_segment_targeted: { type: "STRING" },
                        best_format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image"] },
                        social_angle: { type: "STRING", description: "The specific social media angle/persona for this character." },
                        content_ideas: { type: "ARRAY", items: { type: "STRING" }, description: "3 specific post ideas built around this character." },
                        signature_moment: { type: "STRING", description: "The single most algorithm-ready scene moment involving this character." }
                    },
                    required: ["character_name", "andromeda_hook_served", "audience_segment_targeted", "best_format", "social_angle", "content_ideas", "signature_moment"]
                }
            },

            // ── OPENING WEEKEND WAR ROOM ────────────────────────────────────
            opening_weekend_plan: {
                type: "OBJECT",
                description: "Hour-by-hour and day-by-day Andromeda-optimized drop strategy for release week.",
                properties: {
                    thursday_preview: { type: "STRING", description: "What to post and promote on preview night." },
                    friday_drops: { type: "ARRAY", items: { type: "STRING" }, description: "Opening day post sequence." },
                    saturday_push: { type: "STRING", description: "Saturday amplification strategy — what to boost based on Friday data." },
                    sunday_close: { type: "STRING", description: "Sunday content to drive final weekend numbers." },
                    real_time_signals: { type: "ARRAY", items: { type: "STRING" }, description: "What Andromeda signals to watch in the dashboard to decide which posts to scale." }
                },
                required: ["thursday_preview", "friday_drops", "saturday_push", "sunday_close", "real_time_signals"]
            },

            // ── PAID PROMOTION STRATEGY ─────────────────────────────────────
            paid_promotion_strategy: {
                type: "OBJECT",
                properties: {
                    philosophy: { type: "STRING", description: "1-paragraph Andromeda-era paid philosophy for this specific film." },
                    budget_sequencing: { type: "STRING", description: "How to sequence spend across the campaign phases." },
                    top_paid_drops: {
                        type: "ARRAY",
                        description: "The 5 highest-priority BTS moments to put paid spend behind, ranked.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                rank: { type: "NUMBER" },
                                moment: { type: "STRING" },
                                why: { type: "STRING", description: "Why this specific moment has the strongest Andromeda signal for this film." },
                                suggested_daily_budget: { type: "STRING" },
                                target_metric: { type: "STRING", description: "What result to watch to know if Andromeda is working (not a demographic — a behavioral signal)." }
                            },
                            required: ["rank", "moment", "why", "suggested_daily_budget", "target_metric"]
                        }
                    },
                    fatigue_protocol: { type: "STRING", description: "When and how to rotate creative when Andromeda exhausts an audience segment." }
                },
                required: ["philosophy", "budget_sequencing", "top_paid_drops", "fatigue_protocol"]
            },

            // ── 90-DAY POST-RELEASE PLAN ────────────────────────────────────────
            post_release_90_day: {
                type: "OBJECT",
                description: "Structured week-by-week content strategy for the 90 days following release, designed to sustain Andromeda's audience-finding momentum.",
                properties: {
                    strategic_goal: { type: "STRING", description: "What Andromeda outcome the post-release period is building toward (long tail, VOD discovery, awards, etc.)." },
                    weeks_1_2: {
                        type: "OBJECT",
                        properties: {
                            focus: { type: "STRING", description: "Primary content focus and Andromeda strategy for this window." },
                            posts: { type: "ARRAY", items: {
                                type: "OBJECT",
                                properties: {
                                    content_idea: { type: "STRING" },
                                    format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Story", "Text Overlay"] },
                                    andromeda_hook: { type: "STRING" },
                                    paid_promotion: { type: "BOOLEAN" },
                                    rationale: { type: "STRING", description: "Why this post at this stage extends Andromeda reach." }
                                },
                                required: ["content_idea", "format", "andromeda_hook", "paid_promotion", "rationale"]
                            }}
                        },
                        required: ["focus", "posts"]
                    },
                    weeks_3_4: {
                        type: "OBJECT",
                        properties: {
                            focus: { type: "STRING" },
                            posts: { type: "ARRAY", items: {
                                type: "OBJECT",
                                properties: {
                                    content_idea: { type: "STRING" },
                                    format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Story", "Text Overlay"] },
                                    andromeda_hook: { type: "STRING" },
                                    paid_promotion: { type: "BOOLEAN" },
                                    rationale: { type: "STRING" }
                                },
                                required: ["content_idea", "format", "andromeda_hook", "paid_promotion", "rationale"]
                            }}
                        },
                        required: ["focus", "posts"]
                    },
                    month_2: {
                        type: "OBJECT",
                        properties: {
                            focus: { type: "STRING" },
                            posts: { type: "ARRAY", items: {
                                type: "OBJECT",
                                properties: {
                                    content_idea: { type: "STRING" },
                                    format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Story", "Text Overlay"] },
                                    andromeda_hook: { type: "STRING" },
                                    paid_promotion: { type: "BOOLEAN" },
                                    rationale: { type: "STRING" }
                                },
                                required: ["content_idea", "format", "andromeda_hook", "paid_promotion", "rationale"]
                            }}
                        },
                        required: ["focus", "posts"]
                    },
                    month_3: {
                        type: "OBJECT",
                        properties: {
                            focus: { type: "STRING" },
                            posts: { type: "ARRAY", items: {
                                type: "OBJECT",
                                properties: {
                                    content_idea: { type: "STRING" },
                                    format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image", "Story", "Text Overlay"] },
                                    andromeda_hook: { type: "STRING" },
                                    paid_promotion: { type: "BOOLEAN" },
                                    rationale: { type: "STRING" }
                                },
                                required: ["content_idea", "format", "andromeda_hook", "paid_promotion", "rationale"]
                            }}
                        },
                        required: ["focus", "posts"]
                    },
                    vod_streaming_transition: { type: "STRING", description: "Strategy shift when the film moves to VOD/streaming — how to reset Andromeda targeting for a new audience discovery phase." },
                    long_tail_hooks: { type: "ARRAY", items: { type: "STRING" }, description: "3-5 evergreen content angles that keep the algorithm feeding the film to new viewers past the 90-day window." }
                },
                required: ["strategic_goal", "weeks_1_2", "weeks_3_4", "month_2", "month_3", "vod_streaming_transition", "long_tail_hooks"]
            },

            // ── MAINTENANCE SCHEDULE ────────────────────────────────────────────
            maintenance_schedule: {
                type: "OBJECT",
                description: "Ongoing monthly cadence and performance review protocol for keeping Andromeda fed after the initial campaign window.",
                properties: {
                    monthly_cadence: {
                        type: "OBJECT",
                        description: "Minimum monthly posting requirements to keep the algorithm active on this film.",
                        properties: {
                            minimum_posts_per_month: { type: "NUMBER" },
                            format_mix: { type: "STRING", description: "Recommended format split to maintain (e.g. '60% vertical / 30% carousel / 10% static')." },
                            hook_rotation: { type: "STRING", description: "How to rotate hooks across the month to prevent audience fatigue." },
                            paid_minimum: { type: "STRING", description: "Minimum monthly paid spend recommendation to keep the algorithm warm." }
                        },
                        required: ["minimum_posts_per_month", "format_mix", "hook_rotation", "paid_minimum"]
                    },
                    performance_review_triggers: {
                        type: "ARRAY",
                        description: "Specific signals that should trigger a strategy review — not calendar-based, behavior-based.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                signal: { type: "STRING", description: "What to watch for (e.g. 'Watch-through rate on vertical drops below 35%')." },
                                action: { type: "STRING", description: "What to do when that signal is hit." }
                            },
                            required: ["signal", "action"]
                        }
                    },
                    content_refresh_schedule: {
                        type: "ARRAY",
                        description: "When and why to cut new creative variations from existing assets to re-feed the algorithm.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                timing: { type: "STRING", description: "e.g. 'Month 4, Month 7, Month 12'" },
                                refresh_type: { type: "STRING", description: "What kind of refresh (new edit, reframe hook, format flip, etc.)." },
                                andromeda_rationale: { type: "STRING", description: "Why this refresh re-signals to the algorithm at this stage." }
                            },
                            required: ["timing", "refresh_type", "andromeda_rationale"]
                        }
                    },
                    anniversary_moments: {
                        type: "ARRAY",
                        items: { type: "STRING" },
                        description: "Milestone moments (3-month, 6-month, 1-year) that justify a paid campaign reactivation burst."
                    },
                    evergreen_asset_list: {
                        type: "ARRAY",
                        items: { type: "STRING" },
                        description: "The specific BTS captures or scenes that have the longest algorithmic shelf life and should stay in rotation indefinitely."
                    }
                },
                required: ["monthly_cadence", "performance_review_triggers", "content_refresh_schedule", "anniversary_moments", "evergreen_asset_list"]
            }
        },
        required: ["release_timeline", "bts_capture_manifest", "character_social_angles", "opening_weekend_plan", "paid_promotion_strategy", "post_release_90_day", "maintenance_schedule"]
    };

    return await callGeminiApi(prompt, {
        responseMimeType: "application/json",
        responseSchema: schema,
        // This schema has 7 required top-level sections (release_timeline across
        // multiple phases, bts_capture_manifest, character_social_angles,
        // opening_weekend_plan, paid_promotion_strategy, post_release_90_day,
        // maintenance_schedule), each item carrying 5+ prose fields. 8192 was
        // getting hit mid-response — every failure was the JSON cutting off
        // mid-string/mid-object, not the model writing bad JSON. Raised to the
        // model's actual output ceiling rather than an arbitrary "max".
        maxOutputTokens: 65536
    }, [], GEMINI_2_5_PRO_MODEL);
};


// --- ANDROMEDA REACH SIMULATOR ---
// Takes real campaign inputs and generates phase-by-phase behavioral expectations,
// health signals, and decision triggers — not vanity metric projections.
export const generateAndromedaSimulationLLM = async (filmData, campaignInputs, audienceAnalysis) => {
    const {
        dailyBudget,
        totalDays,
        assetCount,
        platforms,           // e.g. ["Instagram", "Facebook"]
        assetMix,            // e.g. { vertical: 2, carousel: 1, static: 1 }
        objective,           // 'awareness' | 'traffic' | 'video_views'
    } = campaignInputs;

    const totalBudget     = dailyBudget * totalDays;
    const budgetPerAsset  = Math.round(dailyBudget / assetCount);
    const platformStr     = platforms.join(' + ');
    const matrixContext   = (audienceAnalysis?.creative_matrix || [])
        .map((m, i) => `Hook ${i+1} [${m.hook_name}]: ${m.psychological_trigger}`)
        .join('\n') || 'No Creative Matrix loaded.';
    const primaryAud      = audienceAnalysis?.target_audience_breakdown?.primary_audience_profile || 'General audience';

    const prompt = `
    You are a senior Meta media buyer who specializes in film campaigns optimized for the Andromeda algorithmic engine.
    A filmmaker has given you a specific campaign configuration. Your job is to give them a frank, 
    phase-by-phase breakdown of what to realistically expect — what the algorithm will do, 
    what signals prove it is working, what signals prove it is not, and what decisions to make at each stage.
    
    This is NOT a generic media-buying guide. It must be specific to THIS campaign configuration and THIS film.

    FILM: "${filmData.title}" (${filmData.genre})
    LOGLINE: "${filmData.logline || 'Not provided'}"
    PRIMARY AUDIENCE: "${primaryAud}"

    ANDROMEDA CREATIVE MATRIX (the hooks this campaign will run):
    ${matrixContext}

    CAMPAIGN CONFIGURATION:
    - Daily Budget: $${dailyBudget}
    - Total Days: ${totalDays}
    - Total Spend: $${totalBudget}
    - Asset Count: ${assetCount} creatives
    - Budget Per Asset Per Day: ~$${budgetPerAsset}
    - Platforms: ${platformStr}
    - Asset Mix: ${JSON.stringify(assetMix)}
    - Optimization Objective: ${objective}

    ANDROMEDA OPERATING PRINCIPLES (apply these to your analysis):
    1. Broad targeting only. No interest stacks, no demographic boxes. The creative IS the targeting signal.
    2. Learning phase exits at ~50 optimization events per asset. Budget velocity determines how fast this happens.
    3. After learning exit, Andromeda self-selects — CPR drops, reach expands to behavioral clusters.
    4. Creative fatigue is audience-pool exhaustion, not ad blindness. Signs: frequency rises, CPR rises, comment quality drops.
    5. Asset diversity (format, hook, emotional register) prevents the algorithm from over-indexing on one cluster.
    6. The real quality signals are shares, saves, and comment intent — not impressions or raw reach.

    Generate a frank, filmmaker-facing simulation. Use plain language. Avoid jargon where possible.
    Be specific to the $${dailyBudget}/day, ${totalDays}-day, ${assetCount}-asset, ${platformStr} configuration.
    Do not be falsely optimistic. Include realistic lower-bound and upper-bound scenarios.

    Return a single valid JSON object.
    `;

    const schema = {
        type: "OBJECT",
        properties: {

            // Plain-English summary of what this budget/config can realistically accomplish
            campaign_portrait: {
                type: "OBJECT",
                properties: {
                    one_line_verdict: { type: "STRING", description: "One frank sentence on what this campaign configuration can and cannot do for this film." },
                    budget_class: { type: "STRING", enum: ["Micro", "Indie", "Mid", "Competitive"], description: "How Andromeda will classify this budget level." },
                    learning_exit_estimate: { type: "STRING", description: "Estimated days per asset to exit the learning phase at this budget velocity." },
                    algorithm_advantage: { type: "STRING", description: "The specific advantage this film's creative hooks give Andromeda for self-selection." },
                    biggest_risk: { type: "STRING", description: "The single most likely failure mode for this specific configuration." }
                },
                required: ["one_line_verdict", "budget_class", "learning_exit_estimate", "algorithm_advantage", "biggest_risk"]
            },

            // Phase-by-phase behavioral forecast
            phase_breakdown: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        phase_name: { type: "STRING", description: "e.g. 'Learning Phase (Days 1-4)'" },
                        days: { type: "STRING", description: "Day range for this phase" },
                        what_andromeda_is_doing: { type: "STRING", description: "Plain description of the algorithm's internal behavior during this phase." },
                        what_you_will_see: { type: "STRING", description: "What the filmmaker will observe in their dashboard metrics." },
                        expected_metrics: {
                            type: "OBJECT",
                            properties: {
                                cpr_range: { type: "STRING", description: "Expected cost-per-result range (e.g. '$2.50-$4.00')" },
                                daily_results: { type: "STRING", description: "Expected results per day across all assets" },
                                reach: { type: "STRING", description: "Expected daily unique reach" },
                                frequency: { type: "STRING", description: "Expected frequency at end of phase" }
                            },
                            required: ["cpr_range", "daily_results", "reach", "frequency"]
                        },
                        green_signals: { type: "ARRAY", items: { type: "STRING" }, description: "Specific metric behaviors that confirm the algorithm is finding the right audience." },
                        red_signals: { type: "ARRAY", items: { type: "STRING" }, description: "Specific metric behaviors that signal the algorithm is struggling or wasting spend." },
                        decision: { type: "STRING", description: "What action to take at the end of this phase based on results." }
                    },
                    required: ["phase_name", "days", "what_andromeda_is_doing", "what_you_will_see", "expected_metrics", "green_signals", "red_signals", "decision"]
                }
            },

            // Realistic outcome ranges for the full campaign
            outcome_scenarios: {
                type: "OBJECT",
                properties: {
                    conservative: {
                        type: "OBJECT",
                        properties: {
                            label: { type: "STRING" },
                            conditions: { type: "STRING", description: "What has to be true for this outcome." },
                            video_views: { type: "STRING" },
                            link_clicks: { type: "STRING" },
                            shares_saves: { type: "STRING" },
                            audience_clusters_found: { type: "STRING", description: "How many distinct audience behavioral clusters Andromeda likely identified." },
                            cpr_end: { type: "STRING", description: "Expected CPR at end of campaign." },
                            filmmaker_takeaway: { type: "STRING" }
                        },
                        required: ["label", "conditions", "video_views", "link_clicks", "shares_saves", "audience_clusters_found", "cpr_end", "filmmaker_takeaway"]
                    },
                    realistic: {
                        type: "OBJECT",
                        properties: {
                            label: { type: "STRING" },
                            conditions: { type: "STRING" },
                            video_views: { type: "STRING" },
                            link_clicks: { type: "STRING" },
                            shares_saves: { type: "STRING" },
                            audience_clusters_found: { type: "STRING" },
                            cpr_end: { type: "STRING" },
                            filmmaker_takeaway: { type: "STRING" }
                        },
                        required: ["label", "conditions", "video_views", "link_clicks", "shares_saves", "audience_clusters_found", "cpr_end", "filmmaker_takeaway"]
                    },
                    breakout: {
                        type: "OBJECT",
                        properties: {
                            label: { type: "STRING" },
                            conditions: { type: "STRING" },
                            video_views: { type: "STRING" },
                            link_clicks: { type: "STRING" },
                            shares_saves: { type: "STRING" },
                            audience_clusters_found: { type: "STRING" },
                            cpr_end: { type: "STRING" },
                            filmmaker_takeaway: { type: "STRING" }
                        },
                        required: ["label", "conditions", "video_views", "link_clicks", "shares_saves", "audience_clusters_found", "cpr_end", "filmmaker_takeaway"]
                    }
                },
                required: ["conservative", "realistic", "breakout"]
            },

            // The dashboard metrics that actually matter for this specific campaign
            health_dashboard: {
                type: "OBJECT",
                properties: {
                    primary_kpi: { type: "STRING", description: "The single most important metric to watch for THIS campaign objective and asset mix." },
                    watch_list: {
                        type: "ARRAY",
                        description: "The 5-7 specific metrics to monitor daily, in priority order.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                metric: { type: "STRING" },
                                healthy_range: { type: "STRING" },
                                danger_zone: { type: "STRING" },
                                what_it_means: { type: "STRING", description: "Plain-language explanation of what this metric reveals about Andromeda's behavior." }
                            },
                            required: ["metric", "healthy_range", "danger_zone", "what_it_means"]
                        }
                    },
                    the_real_signal: { type: "STRING", description: "The one non-dashboard signal (comment quality, DM volume, saves) that most reliably confirms Andromeda found the right audience for this film." }
                },
                required: ["primary_kpi", "watch_list", "the_real_signal"]
            },

            // Specific decisions to make at each inflection point
            decision_triggers: {
                type: "ARRAY",
                description: "Specific if/then decisions the filmmaker should be ready to make, in chronological order.",
                items: {
                    type: "OBJECT",
                    properties: {
                        trigger: { type: "STRING", description: "The specific condition that prompts a decision (metric-based, not calendar-based)." },
                        timing: { type: "STRING", description: "When this trigger is most likely to appear." },
                        action: { type: "STRING", description: "Exactly what to do when this trigger fires." },
                        do_not: { type: "STRING", description: "The common mistake filmmakers make when this trigger fires." }
                    },
                    required: ["trigger", "timing", "action", "do_not"]
                }
            },

            // What to do with what Andromeda learned for future campaigns
            intelligence_harvest: {
                type: "OBJECT",
                properties: {
                    what_you_will_know: { type: "STRING", description: "What specific audience intelligence this campaign will generate that you did not have before." },
                    winning_asset_signal: { type: "STRING", description: "How to identify which of the ${assetCount} assets won, and why that matters." },
                    next_campaign_inputs: { type: "STRING", description: "How to use what Andromeda learned to structure the NEXT campaign more efficiently." },
                    data_to_export: { type: "ARRAY", items: { type: "STRING" }, description: "Specific reports or data to download from Meta Ads Manager before this campaign data ages out." }
                },
                required: ["what_you_will_know", "winning_asset_signal", "next_campaign_inputs", "data_to_export"]
            }
        },
        required: ["campaign_portrait", "phase_breakdown", "outcome_scenarios", "health_dashboard", "decision_triggers", "intelligence_harvest"]
    };

    return await callGeminiApi(prompt, {
        responseMimeType: "application/json",
        responseSchema: schema
    }, [], GEMINI_2_5_PRO_MODEL);
};


// --- ANDROMEDA CAMPAIGN DEBRIEF ENGINE ---
// Takes real Meta Ads Manager export data (asset-by-asset) and produces
// a frank post-mortem: which assets won, which hooks Andromeda validated,
// what to kill/scale/remix, and how to configure the next campaign.
export const generateAndromedaDebriefLLM = async (filmData, campaignInputs, assetData, audienceAnalysis) => {
    const matrixContext = (audienceAnalysis?.creative_matrix || [])
        .map((m, i) => `Hook ${i+1} [${m.hook_name}]: Trigger=${m.psychological_trigger}, Format bias=${m.visual_direction}`)
        .join('\n') || 'No Creative Matrix loaded.';

    const primaryAud = audienceAnalysis?.target_audience_breakdown?.primary_audience_profile || 'General audience';

    // Serialize the asset rows for the prompt
    const assetRows = assetData.map((a, i) =>
        `Asset ${i+1}: "${a.name}" [${a.format}] [Hook: ${a.hook || 'Unknown'}]
   Spend: $${a.spend} | Results: ${a.results} | CPR: $${a.cpr} | Reach: ${a.reach} | Frequency: ${a.frequency}
   ThruPlay Rate: ${a.thruplay_rate}% | CTR: ${a.ctr}% | Shares: ${a.shares} | Saves: ${a.saves} | Comments: ${a.comments}
   Video 25%: ${a.video_25 || 'N/A'}% | Video 75%: ${a.video_75 || 'N/A'}% | Video 95%: ${a.video_95 || 'N/A'}%`
    ).join('\n\n');

    const totalSpend   = assetData.reduce((s, a) => s + (parseFloat(a.spend) || 0), 0).toFixed(2);
    const totalResults = assetData.reduce((s, a) => s + (parseInt(a.results) || 0), 0);
    const blendedCPR   = totalResults > 0 ? (totalSpend / totalResults).toFixed(2) : 'N/A';

    const prompt = `
    You are a senior Meta media buyer who specializes in film campaigns running on the Andromeda algorithm.
    A filmmaker has just completed a campaign and exported their Meta Ads Manager data.
    Your job is to deliver a frank, asset-by-asset debrief: what Andromeda actually validated, what it rejected,
    and exactly how to reconfigure the next campaign based on what you learned.

    FILM: "${filmData.title}" (${filmData.genre})
    PRIMARY AUDIENCE TARGET: "${primaryAud}"

    ANDROMEDA CREATIVE MATRIX (the hooks these assets were designed to execute):
    ${matrixContext}

    CAMPAIGN CONFIGURATION THAT RAN:
    - Daily Budget: $${campaignInputs.dailyBudget || 'N/A'}
    - Duration: ${campaignInputs.totalDays || 'N/A'} days
    - Platforms: ${(campaignInputs.platforms || []).join(' + ')}
    - Objective: ${campaignInputs.objective || 'N/A'}
    - Total Spend: $${totalSpend}
    - Total Results: ${totalResults}
    - Blended CPR: $${blendedCPR}

    ASSET-BY-ASSET PERFORMANCE DATA:
    ${assetRows}

    ANDROMEDA OPERATING PRINCIPLES (apply these when interpreting the data):
    1. CPR alone does not determine the winner. Andromeda signal quality = CPR trend + share rate + save rate + comment intent + video depth (75%+ completion).
    2. An asset with high reach but low shares/saves found the wrong audience pool — Andromeda delivered volume but not fit.
    3. An asset with lower reach but high save rate and declining CPR is the real winner — Andromeda found fit and was optimizing.
    4. Frequency above 3.5 before day 10 = the audience pool is too small or the creative is polarizing, not resonant.
    5. Video completion to 75%+ is the strongest creative quality signal available.
    6. The hook that drove the most saves is the hook to double down on in the next campaign — saves signal intent, not just attention.
    7. If two assets running the same hook showed divergent results, the format (vertical vs carousel vs static) is the variable — adjust format, keep hook.

    Be direct. Name the winners and losers explicitly. Give specific numbers when referencing assets.
    Do not hedge. If an asset should be killed, say kill it. If a hook should be doubled, say double it.

    Return a single valid JSON object.
    `;

    const schema = {
        type: "OBJECT",
        properties: {

            // Headline verdict
            campaign_verdict: {
                type: "OBJECT",
                properties: {
                    overall_grade: { type: "STRING", enum: ["A", "B", "C", "D", "F"], description: "Andromeda performance grade for the overall campaign." },
                    one_line_verdict: { type: "STRING", description: "One frank sentence on what this campaign proved or failed to prove." },
                    algorithm_behavior: { type: "STRING", description: "What Andromeda actually did with this budget and creative set — did it find fit, stay in learning, or waste spend?" },
                    hooks_validated: { type: "ARRAY", items: { type: "STRING" }, description: "Which Creative Matrix hooks Andromeda confirmed as valid audience signals." },
                    hooks_rejected: { type: "ARRAY", items: { type: "STRING" }, description: "Which hooks Andromeda failed to find an audience for." }
                },
                required: ["overall_grade", "one_line_verdict", "algorithm_behavior", "hooks_validated", "hooks_rejected"]
            },

            // Asset-by-asset breakdown
            asset_rankings: {
                type: "ARRAY",
                description: "All assets ranked by Andromeda signal quality, not raw results.",
                items: {
                    type: "OBJECT",
                    properties: {
                        rank: { type: "NUMBER" },
                        asset_name: { type: "STRING" },
                        verdict: { type: "STRING", enum: ["Scale", "Hold", "Remix", "Kill"], description: "Exactly what to do with this asset." },
                        andromeda_signal_score: { type: "STRING", enum: ["Strong", "Moderate", "Weak", "Dead"], description: "Quality of audience signal this asset generated." },
                        why_it_won_or_lost: { type: "STRING", description: "Specific data-driven explanation referencing the actual metrics." },
                        the_tell: { type: "STRING", description: "The single metric that most clearly explains this asset's Andromeda performance." },
                        action: { type: "STRING", description: "Specific instruction: what to do, change, or test next based on this asset's data." }
                    },
                    required: ["rank", "asset_name", "verdict", "andromeda_signal_score", "why_it_won_or_lost", "the_tell", "action"]
                }
            },

            // Budget reallocation directive
            budget_reallocation: {
                type: "OBJECT",
                properties: {
                    directive: { type: "STRING", description: "Plain-language instruction on how to redistribute the same daily budget across assets for the next run." },
                    kill_list: { type: "ARRAY", items: { type: "STRING" }, description: "Assets to pause immediately." },
                    scale_list: { type: "ARRAY", items: { type: "STRING" }, description: "Assets to increase budget on." },
                    remix_list: {
                        type: "ARRAY",
                        items: {
                            type: "OBJECT",
                            properties: {
                                asset: { type: "STRING" },
                                what_to_change: { type: "STRING", description: "Exactly what element to change — hook, format, hook execution, opening 3 seconds, caption, CTA." },
                                why: { type: "STRING" }
                            },
                            required: ["asset", "what_to_change", "why"]
                        }
                    },
                    recommended_split: { type: "STRING", description: "Specific budget split recommendation for next campaign (e.g. '$280/day on Asset 1, $80/day on remix of Asset 3, kill Assets 2 and 4')." }
                },
                required: ["directive", "kill_list", "scale_list", "remix_list", "recommended_split"]
            },

            // Next campaign blueprint
            next_campaign: {
                type: "OBJECT",
                properties: {
                    hook_strategy: { type: "STRING", description: "Which hook(s) to lead with in the next campaign and why the data supports that choice." },
                    format_strategy: { type: "STRING", description: "Which formats Andromeda responded to best and how to weight the next asset mix." },
                    budget_velocity: { type: "STRING", description: "Whether to change daily budget based on learning phase data — stay flat, increase, or decrease and why." },
                    new_asset_briefs: {
                        type: "ARRAY",
                        description: "2-3 specific new asset briefs to create based on what Andromeda validated.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                brief_name: { type: "STRING" },
                                hook: { type: "STRING" },
                                format: { type: "STRING", enum: ["Vertical Video", "Carousel", "Static Image"] },
                                what_to_capture: { type: "STRING", description: "Specific visual/moment directive based on what the winning data showed Andromeda responds to." },
                                opening_seconds: { type: "STRING", description: "What the first 3 seconds must do based on the completion data from this campaign." },
                                hypothesis: { type: "STRING", description: "What this asset is testing and why the data supports that test." }
                            },
                            required: ["brief_name", "hook", "format", "what_to_capture", "opening_seconds", "hypothesis"]
                        }
                    },
                    expected_improvement: { type: "STRING", description: "Realistic CPR and efficiency improvement to expect in the next campaign if these changes are made, and why." }
                },
                required: ["hook_strategy", "format_strategy", "budget_velocity", "new_asset_briefs", "expected_improvement"]
            },

            // What this data revealed about the audience
            audience_intelligence: {
                type: "OBJECT",
                properties: {
                    who_andromeda_found: { type: "STRING", description: "Based on the engagement patterns, who did Andromeda actually deliver this to? How does that compare to the target?" },
                    unexpected_signals: { type: "ARRAY", items: { type: "STRING" }, description: "Any surprising patterns in the data that reveal something unexpected about the audience." },
                    psychographic_confirmation: { type: "STRING", description: "Which psychographic clusters from the Andromeda audience analysis were confirmed by the actual engagement data?" },
                    data_gaps: { type: "ARRAY", items: { type: "STRING" }, description: "What questions this campaign data cannot answer that the next campaign should be designed to answer." }
                },
                required: ["who_andromeda_found", "unexpected_signals", "psychographic_confirmation", "data_gaps"]
            }
        },
        required: ["campaign_verdict", "asset_rankings", "budget_reallocation", "next_campaign", "audience_intelligence"]
    };

    return await callGeminiApi(prompt, {
        responseMimeType: "application/json",
        responseSchema: schema
    }, [], GEMINI_2_5_PRO_MODEL);
};

// --- GREENLIGHT COVERAGE ENGINE ---
// aiService.js

export const generateGreenlightCoverageLLM = async (scriptContent, filmData, credits, deductCredit, setNotification, projectFormat = 'Feature Film') => {
    const creditCost = 8; 
    
    if (credits < creditCost) {
        setNotification({ type: 'error', message: `Insufficient credits. Greenlight Coverage requires ${creditCost} credits.` });
        throw new Error('Insufficient credits');
    }

    // --- DYNAMIC PERSONA & RUBRIC SELECTOR ---
    let rubric = "";
    let persona = "";

    if (projectFormat === 'TV Pilot') {
        persona = "You are a Senior VP of Development at a premium streamer (HBO, Netflix, Apple TV+).";
        rubric = `
        EVALUATION CRITERIA (TV PILOT):
        1. SERIES ENGINE: Does this pilot set up 5+ seasons of conflict? Or does the story end here?
        2. PILOT CLIFFHANGER: Does the ending force the viewer to click "Next Episode"?
        3. WORLD BUILDING: Is the setting rich enough to sustain 10 hours of content?
        4. ENSEMBLE: Are there B and C stories setup?
        
        CRITICAL: Do NOT judge this as a self-contained movie. Judge it on its potential to launch a franchise.
        `;
    } else if (projectFormat === 'Vertical Series') {
        persona = "You are a Content Acquisition Lead for ReelShort or TikTok Series.";
        rubric = `
        EVALUATION CRITERIA (VERTICAL / SHORT-FORM):
        1. THE HOOK: Does the first 10 seconds grab attention immediately?
        2. PACING: Is it fast, dialogue-heavy, and high-conflict? (No slow pans or long setups).
        3. RETENTION: Does every "episode" break end on a massive cliffhanger?
        4. GENRE FIT: Is it high-concept melodrama, thriller, or romance?
        
        CRITICAL: Judge this on "Swipe Retention" and "Viral Potential", not cinematic nuance.
        `;
    } else {
        // Default: Feature Film
        persona = "You are a ruthless Senior Vice President of Acquisitions at a major studio (A24, Neon, Blumhouse).";
        rubric = `
        EVALUATION CRITERIA (FEATURE FILM):
        1. THEATRICALITY: Is this worth a $20 ticket?
        2. 3-ACT STRUCTURE: Does it have a defined beginning, middle, and satisfying resolution?
        3. COMMERCIAL APPEAL: Can this play globally?
        `;
    }

    const prompt = `
    ${persona}
    
    Your job is to assess the attached script for a "Greenlight" decision based on its format: **${projectFormat}**.
    
    FILM/PROJECT: "${filmData.title}"
    GENRE: "${filmData.genre}"
    
    ${rubric}

    SCRIPT CONTENT (Excerpt):
    ${scriptContent.substring(0, 20000)} ... [truncated]

    INSTRUCTIONS:
    1. ANALYZE: Be brutal and honest about viability for this specific format.
    2. PITCH: Write a "Financier Pitch Packet".

    Return a valid JSON object with this EXACT structure:
    {
        "verdict": "Greenlight" | "Yellow Light" | "Red Light",
        "logline_grade": "A+" to "F",
        "commercial_score": number (0-100),
        "creative_score": number (0-100),
        "the_verdict_summary": "A 2-sentence executive summary of the decision.",
        "financier_pitch": {
            "logline": "A polished, sales-ready logline (max 40 words).",
            "theme": "The deeper thematic resonance.",
            "tone": "Visceral description of the vibe. Use comps."
        },
        "strengths": ["List of 3 distinct strengths for this format"],
        "weaknesses": ["List of 3 distinct risks for this format"],
        "major_issues": [
             { "issue": "Short Label", "fix": "Specific actionable advice." }
        ],
        "character_pass": {
             "protagonist_rating": number (0-10),
             "antagonist_rating": number (0-10),
        },
        "star_power_needed": {
             "verdict": "Does this require A-List talent?",
             "suggested_names": ["Name 1", "Name 2", "Name 3"]
        }
    }
    `;

    try {
        const result = await callGeminiApi(prompt, { responseMimeType: "application/json" }, [], GEMINI_2_5_PRO_MODEL);
        await deductCredit(creditCost);
        setNotification({ type: 'success', message: `Coverage generated for ${projectFormat}. ${creditCost} credits used.` });
        return result;
    } catch (error) {
        console.error('Greenlight analysis failed:', error);
        throw error;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// CALENDAR STRATEGY COMPILER — Social Media Playbook → blackbox_calendar_strategy.json
// ═══════════════════════════════════════════════════════════════════════════
// Deliberately NOT an LLM call. By the time a Social Media Playbook exists,
// every piece of creative content this needs — hooks, formats, post ideas,
// character angles — has already been generated and reviewed. Compiling it
// into the calendar's JSON shape is date math and a vocabulary translation,
// not new writing. Doing that in code means zero risk of the compiler
// silently rewriting or dropping content the Playbook already got right,
// and zero extra credits spent to restate what's already been said well.
//
// Downstream contract this has to satisfy (see blackbox_gemini_drafter.py
// and StrategyCalendar.tsx on the Campaign Engine side):
//   { _meta: { total_posts, description },
//     phases: { "1": {label,period,directive,months,post_count}, "2": {...}, "3": {...} },
//     projects: { [title]: hexColor },
//     posts: [ { phase, month, week, project, project_color, hook, detail,
//                format: "Reel"|"Carousel"|"Single"|"Profile", requires_video } ] }

const CALENDAR_FORMAT_MAP = {
    'Vertical Video': 'Reel',
    'Story':          'Reel',
    'Carousel':       'Carousel',
    'Static Image':   'Single',
    'Text Overlay':   'Single',
};

const PHASE_LABELS = { 1: 'Establish', 2: 'Community', 3: 'Amplify' };
const POST_HOURS = ['09:00', '12:00', '19:00']; // matches the rotation blackbox_gemini_drafter.py already uses

function _addDays(date, n) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + n);
    return d;
}

function _monthLabel(date) {
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function _weekLabel(date) {
    return `Week of ${date.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

function _periodLabel(dates) {
    if (dates.length === 0) return '';
    const sorted = [...dates].sort((a, b) => a - b);
    const start = sorted[0], end = sorted[sorted.length - 1];
    const fmt = d => d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    return start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth()
        ? fmt(start)
        : `${fmt(start)} – ${fmt(end)}`;
}

// Every pre-release timeline phase from the Playbook gets bucketed into one
// of Blackbox's 3 numbered phases by what kind of content it actually is —
// matching the same Establish/Community/Amplify semantics the drafter's own
// system prompt uses, not just chronological order.
function _bucketPhase(phaseLabel) {
    const s = (phaseLabel || '').toLowerCase();
    if (s.includes('festival') || s.includes('award') || s.includes('release')) return 3;
    // Check pre-production before the general 'production' check below —
    // "pre-production" contains "production" as a substring, so testing the
    // general case first would misfile it into Community instead of Establish.
    if (s.includes('pre-production') || s.includes('pre production')) return 1;
    if (s.includes('production') || s.includes('on-set') || s.includes('post-production')) return 2;
    return 1;
}

/**
 * Spread `count` posts evenly across [startDate, endDate], rotating through
 * POST_HOURS the same way the Modal drafter does, so a calendar compiled
 * here and a calendar drafted server-side read the same rhythm.
 */
function _spreadDates(startDate, endDate, count) {
    if (count <= 0) return [];
    const totalMs = Math.max(endDate.getTime() - startDate.getTime(), 0);
    const stepMs = count > 1 ? totalMs / (count - 1) : 0;
    return Array.from({ length: count }, (_, i) => {
        const d = new Date(startDate.getTime() + stepMs * i);
        const hour = POST_HOURS[i % POST_HOURS.length];
        return { date: d, iso: `${d.toISOString().split('T')[0]}T${hour}:00Z` };
    });
}

// Rough word-overlap score (0-1) between a candidate post's text and a
// paid_promotion_strategy top_paid_drops "moment" description — used only
// as a fallback for post sources (character angles, opening-weekend posts)
// that don't carry their own explicit paid_promotion flag from the Playbook.
function _wordOverlapScore(momentText, postText) {
    const words = s => (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length > 3);
    const momentWords = new Set(words(momentText));
    const postWords = words(postText);
    if (momentWords.size === 0 || postWords.length === 0) return 0;
    const overlap = postWords.filter(w => momentWords.has(w)).length;
    return overlap / postWords.length;
}

/**
 * compileCalendarStrategyJSON(filmData, audienceAnalysis, socialPlaybook)
 * → the exact JSON shape blackbox_calendar_strategy.json expects, ready to
 *   upload via "Add strategy brief" in the Campaign Engine.
 *
 * Throws (rather than guessing) when a required upstream piece is missing —
 * a calendar built on a silent fallback is worse than one that refuses to run.
 */
export function compileCalendarStrategyJSON(filmData, audienceAnalysis, socialPlaybook) {
    if (!filmData?.title) {
        throw new Error('Missing project title — set it in Project Parameters before exporting the calendar.');
    }
    if (!filmData?.releaseDate) {
        throw new Error('Missing release date — set Target Release Date in Project Parameters. Every phase in this calendar anchors to it.');
    }
    if (!socialPlaybook?.release_timeline?.length) {
        throw new Error('No Social Media Playbook found — run Generate Playbook in Campaign Intelligence first.');
    }

    const releaseDate = new Date(`${filmData.releaseDate}T00:00:00Z`);
    if (Number.isNaN(releaseDate.getTime())) {
        throw new Error(`Release date "${filmData.releaseDate}" isn't a valid date.`);
    }

    const projectTitle = filmData.title;
    const projectColor = '#F59E0B'; // brand amber — single-project campaigns get one legend color
    const posts = [];

    // ── 1. Pre-release timeline (release_timeline) — bucketed into phases 1/2 ──
    // Playbook phases are chronological and arbitrarily granular ("Pre-Production
    // (Weeks -16 to -12)", "Production (On-Set)", "Festival/Awards Run"...).
    // We bucket each into Establish/Community/Amplify by content type, then lay
    // pre-release buckets 1 and 2 back-to-back across the run-up to release,
    // proportional to how many posts each contributes.
    const timelineByBucket = { 1: [], 2: [] }; // bucket 3 (festival/release) is handled separately, anchored at release
    const releaseWeekEntries = [];
    socialPlaybook.release_timeline.forEach(entry => {
        const bucket = _bucketPhase(entry.phase);
        if (bucket === 3) {
            releaseWeekEntries.push(entry);
        } else {
            timelineByBucket[bucket].push(entry);
        }
    });

    // NOTE: counts posts within each bucket's entries, not the entries
    // themselves — a bucket with one entry holding 5 posts should get 5x
    // the window of a bucket with one entry holding 1 post, not an equal split.
    const _postsIn = entries => entries.reduce((sum, e) => sum + (e.posts?.length || 0), 0);
    const preReleaseCount = _postsIn(timelineByBucket[1]) + _postsIn(timelineByBucket[2]);
    // Campaign start: 16 weeks out is the Playbook's own convention (see its
    // "Pre-Production (Weeks -16 to -12)" phase label) — used as a sane default
    // run-up length when we don't have an actual production start date.
    const campaignStart = _addDays(releaseDate, -16 * 7);
    // Phase 1 and 2 pre-release windows split proportionally by post volume,
    // in chronological order, ending 3 days before release (release week itself
    // is handled separately, anchored precisely to opening_weekend_plan).
    const preReleaseEnd = _addDays(releaseDate, -3);
    const totalPreReleaseSpanMs = preReleaseEnd.getTime() - campaignStart.getTime();
    // Release week window — needed both by the opening-weekend section below and
    // by the BTS capture-manifest folding step, so it's computed once, up front.
    const thursday = _addDays(releaseDate, -1);

    // Bucket 1/2 date windows, filled in as each bucket is laid out below —
    // the BTS capture-manifest step reuses these instead of computing its own,
    // so capture posts land in the same window as the timeline posts they support.
    const bucketWindows = {};

    let cursor = campaignStart;
    [1, 2].forEach(bucketNum => {
        const entries = timelineByBucket[bucketNum];
        const bucketPostCount = _postsIn(entries);
        if (bucketPostCount === 0) return;
        const bucketSpanMs = totalPreReleaseSpanMs * (bucketPostCount / Math.max(preReleaseCount, 1));
        const bucketEnd = new Date(cursor.getTime() + bucketSpanMs);
        bucketWindows[bucketNum] = { start: cursor, end: bucketEnd };
        const dates = _spreadDates(cursor, bucketEnd, bucketPostCount);
        let i = 0;
        entries.forEach(entry => {
            (entry.posts || []).forEach(p => {
                const { date } = dates[i++];
                posts.push({
                    phase: bucketNum,
                    month: _monthLabel(date),
                    week: _weekLabel(date),
                    project: projectTitle,
                    project_color: projectColor,
                    hook: p.content_idea,
                    detail: `${p.caption_direction}${p.andromeda_hook ? ` — serves hook: ${p.andromeda_hook}` : ''}`,
                    format: CALENDAR_FORMAT_MAP[p.format] || 'Single',
                    requires_video: CALENDAR_FORMAT_MAP[p.format] === 'Reel',
                    paid_boost: !!p.paid_promotion,
                    paid_rationale: p.paid_promotion ? (p.paid_rationale || null) : null,
                    _date: date,
                });
            });
        });
        cursor = bucketEnd;
    });

    // ── 2. Character social angles — folded in as Phase 2 "Profile" posts ──
    // These already exist as fully-formed content ideas tied to hooks; Profile
    // is the one calendar format the pre-release timeline mapping above never
    // produces, and it's exactly what these are.
    if (socialPlaybook.character_social_angles?.length) {
        const charDates = _spreadDates(
            _addDays(campaignStart, 7),
            preReleaseEnd,
            socialPlaybook.character_social_angles.length
        );
        socialPlaybook.character_social_angles.forEach((c, i) => {
            const { date } = charDates[i];
            const idea = c.content_ideas?.[0] || c.signature_moment || `${c.character_name} — ${c.social_angle}`;
            posts.push({
                phase: 2,
                month: _monthLabel(date),
                week: _weekLabel(date),
                project: projectTitle,
                project_color: projectColor,
                hook: idea,
                detail: `${c.social_angle} — signature moment: ${c.signature_moment || 'n/a'} — serves hook: ${c.andromeda_hook_served}`,
                format: 'Profile',
                requires_video: false,
                paid_boost: false,
                paid_rationale: null,
                _date: date,
            });
        });
    }

    // ── 2b. BTS Capture Manifest — folded in as scheduled posts, not just a
    // separate CSV export. Bucketed the same way timeline entries are and
    // sharing that bucket's actual date window, so a post and the capture
    // directive behind it land in the same stretch of the calendar.
    if (socialPlaybook.bts_capture_manifest?.length) {
        const CAPTURE_FORMAT_MAP = {
            'Vertical Video Clip': 'Reel',
            'Reaction Shot': 'Reel',
            'Time-Lapse': 'Reel',
            'Carousel Sequence': 'Carousel',
            'Static Still': 'Single',
        };
        const captureByBucket = { 1: [], 2: [], 3: [] };
        socialPlaybook.bts_capture_manifest.forEach(item => {
            captureByBucket[_bucketPhase(item.posting_phase)].push(item);
        });
        [1, 2, 3].forEach(bucketNum => {
            const items = captureByBucket[bucketNum];
            if (!items.length) return;
            // Bucket 3 capture items ("Release Week...") anchor to release week
            // itself; buckets 1/2 reuse the window their timeline posts already got.
            const window = bucketNum === 3
                ? { start: thursday, end: _addDays(releaseDate, 2) }
                : bucketWindows[bucketNum];
            if (!window) return; // no timeline posts landed in this bucket — nothing to anchor to
            const dates = _spreadDates(window.start, window.end, items.length);
            items.forEach((item, i) => {
                const { date } = dates[i];
                posts.push({
                    phase: bucketNum,
                    month: _monthLabel(date),
                    week: _weekLabel(date),
                    project: projectTitle,
                    project_color: projectColor,
                    hook: item.moment,
                    detail: `${item.caption_direction}${item.andromeda_hook ? ` — serves hook: ${item.andromeda_hook}` : ''}`,
                    format: CAPTURE_FORMAT_MAP[item.capture_type] || 'Single',
                    requires_video: CAPTURE_FORMAT_MAP[item.capture_type] === 'Reel',
                    asset_type: item.capture_type,
                    emotional_register: item.emotional_register || null,
                    paid_boost: !!item.paid_drop,
                    paid_rationale: item.paid_drop ? (item.paid_drop_rationale || null) : null,
                    _date: date,
                });
            });
        });
    }

    // ── 3. Release week — anchored precisely to opening_weekend_plan + any
    // release_timeline entries bucketed as phase 3 ──────────────────────────
    const owp = socialPlaybook.opening_weekend_plan;
    if (owp) {
        const weekendPosts = [
            { day: thursday,                   hook: 'Preview night drop', detail: owp.thursday_preview, format: 'Reel' },
            ...(owp.friday_drops || []).map(text => ({ day: releaseDate, hook: text, detail: 'Opening day sequence.', format: 'Single' })),
            { day: _addDays(releaseDate, 1),   hook: 'Saturday amplification push', detail: owp.saturday_push, format: 'Carousel' },
            { day: _addDays(releaseDate, 2),   hook: 'Sunday close-out', detail: owp.sunday_close, format: 'Single' },
        ].filter(p => p.detail);
        weekendPosts.forEach(p => {
            posts.push({
                phase: 3,
                month: _monthLabel(p.day),
                week: _weekLabel(p.day),
                project: projectTitle,
                project_color: projectColor,
                hook: p.hook,
                detail: p.detail,
                format: p.format,
                requires_video: p.format === 'Reel',
                paid_boost: false,
                paid_rationale: null,
                _date: p.day,
            });
        });
    }
    // Festival/awards timeline entries also land in phase 3, spread across
    // whatever window remains between campaign start and release — festivals
    // typically run ahead of a theatrical release, not after it.
    const festivalPostCount = releaseWeekEntries.reduce((sum, e) => sum + (e.posts?.length || 0), 0);
    if (festivalPostCount > 0) {
        const dates = _spreadDates(_addDays(campaignStart, 21), preReleaseEnd, festivalPostCount);
        let i = 0;
        releaseWeekEntries.forEach(entry => {
            (entry.posts || []).forEach(p => {
                const { date } = dates[i++];
                posts.push({
                    phase: 3,
                    month: _monthLabel(date),
                    week: _weekLabel(date),
                    project: projectTitle,
                    project_color: projectColor,
                    hook: p.content_idea,
                    detail: `${p.caption_direction}${p.andromeda_hook ? ` — serves hook: ${p.andromeda_hook}` : ''}`,
                    format: CALENDAR_FORMAT_MAP[p.format] || 'Single',
                    requires_video: CALENDAR_FORMAT_MAP[p.format] === 'Reel',
                    paid_boost: !!p.paid_promotion,
                    paid_rationale: p.paid_promotion ? (p.paid_rationale || null) : null,
                    _date: date,
                });
            });
        });
    }

    // ── 4. Post-release 90-day plan — phase 3 continues past release ───────
    const postRelease = socialPlaybook.post_release_90_day;
    const postReleaseWindows = postRelease ? [
        { key: 'weeks_1_2', start: _addDays(releaseDate, 3),  end: _addDays(releaseDate, 14) },
        { key: 'weeks_3_4', start: _addDays(releaseDate, 15), end: _addDays(releaseDate, 28) },
        { key: 'month_2',   start: _addDays(releaseDate, 29), end: _addDays(releaseDate, 59) },
        { key: 'month_3',   start: _addDays(releaseDate, 60), end: _addDays(releaseDate, 90) },
    ] : [];
    postReleaseWindows.forEach(({ key, start, end }) => {
        const block = postRelease[key];
        if (!block?.posts?.length) return;
        const dates = _spreadDates(start, end, block.posts.length);
        block.posts.forEach((p, i) => {
            const { date } = dates[i];
            posts.push({
                phase: 3,
                month: _monthLabel(date),
                week: _weekLabel(date),
                project: projectTitle,
                project_color: projectColor,
                hook: p.content_idea,
                detail: `${p.rationale}${p.andromeda_hook ? ` — serves hook: ${p.andromeda_hook}` : ''}`,
                format: CALENDAR_FORMAT_MAP[p.format] || 'Single',
                requires_video: CALENDAR_FORMAT_MAP[p.format] === 'Reel',
                paid_boost: !!p.paid_promotion,
                paid_rationale: p.paid_promotion ? (p.paid_rationale || null) : null,
                _date: date,
            });
        });
    });

    // ── Fallback paid-boost matching — posts from sources that don't carry
    // their own paid_promotion flag (character angles, opening-weekend posts)
    // get checked against paid_promotion_strategy's ranked top_paid_drops, so
    // "which posts should get paid spend" isn't only answerable for the two
    // post types the Playbook happened to tag directly.
    const topPaidDrops = socialPlaybook.paid_promotion_strategy?.top_paid_drops || [];
    if (topPaidDrops.length) {
        posts.forEach(post => {
            if (post.paid_boost) return; // already explicitly flagged upstream
            const best = topPaidDrops
                .map(drop => ({ drop, score: Math.max(_wordOverlapScore(drop.moment, post.hook), _wordOverlapScore(drop.moment, post.detail)) }))
                .sort((a, b) => b.score - a.score)[0];
            if (best && best.score >= 0.4) {
                post.paid_boost = true;
                post.paid_rationale = best.drop.why;
                post.paid_daily_budget = best.drop.suggested_daily_budget;
            }
        });
    }

    if (posts.length === 0) {
        throw new Error('The Playbook produced no schedulable posts — nothing to compile into a calendar.');
    }

    // ── Build phases object from the posts actually placed in each bucket ──
    const phases = {};
    [1, 2, 3].forEach(num => {
        const phasePosts = posts.filter(p => p.phase === num);
        const dates = phasePosts.map(p => p._date).filter(d => d instanceof Date && !Number.isNaN(d.getTime()));
        // Directive: pull the strategic goal(s) that actually landed in this
        // bucket, falling back to the matching Andromeda creative-matrix hook
        // for phase framing when the Playbook itself didn't supply one here.
        const relevantGoals = socialPlaybook.release_timeline
            .filter(e => _bucketPhase(e.phase) === num)
            .map(e => e.strategic_goal)
            .filter(Boolean);
        const fallbackDirective = audienceAnalysis?.creative_matrix?.[num - 1]
            ? `${audienceAnalysis.creative_matrix[num - 1].hook_name}: ${audienceAnalysis.creative_matrix[num - 1].psychological_trigger}`
            : `Phase ${num} — ${PHASE_LABELS[num]}.`;
        phases[String(num)] = {
            label: PHASE_LABELS[num],
            period: _periodLabel(dates),
            directive: (relevantGoals[0] || fallbackDirective)
                + (num === 3 && postRelease?.strategic_goal ? ` Post-release: ${postRelease.strategic_goal}` : ''),
            months: [...new Set(phasePosts.map(p => p.month))],
            post_count: phasePosts.length,
        };
    });

    // Chronological order, then strip the internal-only _date bookkeeping field
    // and emit it as an explicit ISO `date` string, with a sequential `id` — the
    // Campaign Engine's importer now schedules off `date` directly rather than
    // parsing month/week text, so both are required, not optional extras.
    const cleanPosts = posts
        .slice()
        .sort((a, b) => a._date - b._date)
        .map(({ _date, ...rest }, i) => ({
            id: `p${i + 1}`,
            date: _date.toISOString().split('T')[0],
            ...rest,
        }));

    return {
        _meta: {
            total_posts: cleanPosts.length,
            description: audienceAnalysis?.executive_summary?.[0]
                || `${projectTitle} social campaign, anchored to a ${filmData.releaseDate} release.`,
        },
        phases,
        projects: { [projectTitle]: projectColor },
        posts: cleanPosts,
        // Not part of the required schema — extra keys are ignored by
        // isValidStrategyShape's upload check but preserved here so the
        // maintenance/evergreen guidance isn't lost, just not force-fit into
        // dated posts it was never meant to be.
        maintenance_schedule: socialPlaybook.maintenance_schedule || null,
        // Strategic paid-spend guidance (philosophy, budget sequencing, fatigue
        // protocol) — the ranked top_paid_drops list is also what per-post
        // paid_boost/paid_rationale above were matched against.
        paid_strategy: socialPlaybook.paid_promotion_strategy || null,
    };
}

/**
 * compileCampaignExportJSON(filmData, audienceAnalysis, scriptAnalysis, socialPlaybook)
 * → the full "Generate Campaign" export: everything compileCalendarStrategyJSON
 *   produces (unchanged, top-level, so the Campaign Engine's existing "Add
 *   strategy brief" importer keeps working with zero changes on that end) PLUS
 *   an `audience_intelligence` section — the Andromeda audience profile, creative
 *   matrix, and script-derived hooks/quotes/character angles the calendar's posts
 *   were built from. One file, two tabs: know your audience, then the plan itself.
 *
 * scriptAnalysis is optional — Story & Hooks may not have been run even though
 * a playbook exists from an older generation; audience_intelligence just omits
 * the script-derived fields it doesn't have rather than failing.
 */
export function compileCampaignExportJSON(filmData, audienceAnalysis, scriptAnalysis, socialPlaybook) {
    const calendar = compileCalendarStrategyJSON(filmData, audienceAnalysis, socialPlaybook);

    const audience_intelligence = {
        executive_summary: audienceAnalysis?.executive_summary || [],
        target_audience: audienceAnalysis?.target_audience_breakdown || null,
        creative_matrix: audienceAnalysis?.creative_matrix || [],
        asset_allocation: audienceAnalysis?.asset_allocation || null,
        top_us_theatrical_markets: audienceAnalysis?.top_us_theatrical_markets || [],
        top_film_festivals: audienceAnalysis?.top_film_festivals || null,
        // Script-derived grounding for whatever writes the actual post copy —
        // omitted cleanly if Story & Hooks hasn't been run.
        script_hooks: scriptAnalysis?.campaign_hooks || null,
        quotable_dialogue: (scriptAnalysis?.quotable_dialogue || []).slice(0, 8),
        genre_positioning: scriptAnalysis?.genre_positioning || null,
    };

    return {
        _meta: { ...calendar._meta, includes_audience_intelligence: true },
        audience_intelligence,
        phases: calendar.phases,
        projects: calendar.projects,
        posts: calendar.posts,
        maintenance_schedule: calendar.maintenance_schedule,
        paid_strategy: calendar.paid_strategy,
    };
}